import Image from "next/image";
import { cn } from "@/lib/utils";
import { env } from "@/lib/env";
import { NodeLayout } from "@/components/nodes/layout";
import { useInViewport } from "@/hooks/use-in-viewport";

import { download } from "@/lib/download";
import { handleError } from "@/lib/error/handle";
import { videoModels } from "@flowmuse/models/video";
import { getDefaultModelId, getLastSelectedModelId, getPresetAspectRatio, getPresetModelId, getPresetModelParams, rememberLastSelectedModelId } from "@flowmuse/models/shared/utils";
import { getUiOptionsForModel, getHiddenDefaults, type UiOption } from "@flowmuse/models/video/ui-options";
import { getVideosFromVideoNodes, getAllImagesFromNodes, resolveNodeAssetRefUrls } from "@/lib/xyflow";
import { useProject } from "@/providers/project";
import { useSubscription, filterModelsClient } from "@/providers/subscription";
import { markIncompatibleModels } from "@/components/nodes/model-access";
import { useBeforeUnload } from "@/hooks/use-before-unload";
import { useEstimatedBilling } from "@/hooks/use-estimated-credits";
import {
  useNodeConnections,
  useReactFlow,
  useStore,
} from "@xyflow/react";
import { createVideoHandleGenerate, PrecheckError } from "./handle-generate";
import { patchHistoryItemById } from "@/components/nodes/shared/media-history";
import { cancelGeneration } from "@/lib/cancel-generation";
import { useVideoGuidance } from "./use-video-guidance";
import { useVideoGuidanceActions } from "./use-video-guidance-actions";
import { VideoGuidance } from "./video-guidance";
import {
  Loader2Icon,
  Image as ImageIcon,
  Video as VideoIcon,
  Music as MusicIcon,
  XIcon,
  Play as PlayIcon,
  Pause as PauseIcon,
  Package,
  Maximize2,
  Zap,
} from "lucide-react";
import {
  type ComponentProps,
  memo,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { toast } from "sonner";
import { mutate } from "swr";
import type { VideoNodeProps } from ".";
import { useVideoRealtime } from "./handle-realtime";
import { useVideoPolling } from "./handle-polling";
import { useAutoCancelHandler, useAutoGenerateHandler } from "@/components/canvas/utils/auto-generate-bus";
import { nanoid } from "nanoid";
import { useExtractFrame } from "@/hooks/use-extract-frame";
// Phase 9.7: per-id selectors replaced with merged shallow-equal store slice.
import { useIsMultiSelect } from "@/components/canvas/utils/multi-select-store";
import { useIsCanvasPositionInteracting } from "@/components/canvas/utils/drag-state-store";
import { shallow } from "zustand/shallow";
import { Switch } from "@/components/ui/switch";
import { OptionRemark } from "@/components/nodes/shared/option-remark";
import { shouldSkipNextImageOptimization, useImageVariant } from "@/lib/image-variant";
import { videoPosterUrl } from "@flowmuse/api-core/media-shared";
import { VariantImage } from "@/components/variant-image";
import {
  VideoAspectRatioSelector,
  VideoDurationSelector,
  VideoQualitySelector,
} from "@/components/nodes/video/video-params-selectors";
import { InlineComboboxSelect } from "@/components/nodes/image/inline-combobox-select";
import { estimateVideoBilling } from "@flowmuse/models/cost/video-estimate";
// 新增：共享工具栏与控件
import {
  ThreeRowToolbar,
  InstructionInput, type InstructionInputHandle,
  ModelItem,
  GenerateItem,
  DownloadItem,
  HistoryItem,
  AdvancedToggleButton,
  AdvancedInlinePanel,
  InlineOptionsRow,
  NodeSurface,
  ProcessingSurface,
  useLinearProgress,
  useIncomingImageInputs,
  PrecheckDialog,
  TextModeImageDialog,
} from "@/components/nodes/shared";
import { useI18n } from "@/providers/i18n";
import { EditWithPinturaVideoOnDoubleClick } from "@/components/nodes/video/pintura-edit-on-double-click";
import { PreviewVideoOnClick } from "@/components/nodes/video/preview-on-click";
import { VideoTopToolbar } from "@/components/nodes/video/video-top-toolbar";
import { VideoPreviewDialog } from "@/components/nodes/video/preview-dialog";
import { VideoCollapsedSummary } from "@/components/nodes/video/collapsed-summary";
import { MultiShotEditor } from "@/components/nodes/video/multi-shot-editor";
import { MultiVideoMenuPanel, MultiVideoTrigger, useMultiVideoPicker } from "./multi-video-picker";
import { isAssetRefUrl, resolveAssetRefPreview } from "@flowmuse/models/shared/asset-ref";
import {
  type InlineAttachment,
  type InlineMediaConfig,
  detectMediaType,
  parseBytes,
  getMediaDuration,
  getMediaDurationFromUrl,
  getVideoPixels,
  validateMediaItem,
  buildAcceptFromConfig,
  countByType,
  canAddMore,
  classifyNodeMediaType,
  defaultInlineMediaForMode,
  mergeInlineMedia,
} from "@flowmuse/models/inline-attachments";
import { uploadFile } from "@/lib/upload";
import { useAuth, useUser } from "@clerk/nextjs";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { SortableGridItem } from "@/components/nodes/shared/sortable-grid-item";
import { DraggableMediaGrid } from "@/components/nodes/shared/draggable-media-grid";
import { rewriteMentionsAfterRemoval, rewriteMentionsAfterReorder, type MentionLabels } from "@/components/nodes/shared/mention-mirror";
import { useDeferredInstructionsRewrite } from "@/components/nodes/shared/use-deferred-instructions-rewrite";
import { useFlushInstructionsOnUnmount } from "@/components/nodes/shared/use-flush-instructions-on-unmount";
import { requestLibraryPick, type LibraryPickResult } from "@/components/canvas/utils/asset-folders-events";
import { SubjectPickerDialog } from "@/components/nodes/shared/subject-picker-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UploadIcon, LibraryBigIcon, UserSquare } from "lucide-react";

const PICKER_MENU_CN = 'fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-xl min-w-[160px] p-1 z-[200]';
const PICKER_ITEM_CN = 'rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-medium';

const isVideoLikeUrl = (u: string) =>
  /(\.mp4|\.webm|\.mov|\.m4v|\.ogv|\.ogg|\.avi|\.mkv)(\?|#|$)/i.test(u);

const NODE_BG_STYLE = { backgroundColor: "var(--node-bg)" } as const;
const REF_THUMB_TRANSFORM = { width: 96, height: 96, resize: "cover", quality: 70 } as const;
const POSTER_TRANSFORM = { width: 1024, quality: 80 } as const;

type EffectiveConfig = {
  aspectRatios?: string[];
  durations?: number[];
  qualities?: string[];
  qualityByDuration?: Record<number, string[]>;
  inlineMedia?: InlineMediaConfig;
};
function getEffectiveConfig(model: any, frameMode?: string): EffectiveConfig {
  const base: EffectiveConfig = {
    aspectRatios: model?.aspectRatios,
    durations: model?.durations,
    qualities: model?.qualities,
    qualityByDuration: model?.qualityByDuration,
    inlineMedia: model?.inlineMedia,
  };
  if (!frameMode || !model?.configByMode) return base;
  const override = model.configByMode[frameMode];
  if (!override) return base;
  return {
    aspectRatios: override.aspectRatios ?? base.aspectRatios,
    durations: override.durations ?? base.durations,
    qualities: override.qualities ?? base.qualities,
    qualityByDuration: override.qualityByDuration ?? base.qualityByDuration,
    inlineMedia: mergeInlineMedia(base.inlineMedia, override.inlineMedia),
  };
}

/**
 * 解析真实分辨率：统一以 `size = "宽x高"` 字符串承载（与 image 的 generated.size 一致）。
 * 兼容旧数据：优先读 size 字符串，回退到旧的数字 width/height 字段。
 */
function parseVideoDimensions(source: any): { width: number; height: number } | null {
  const raw = source?.generated?.size ?? source?.size;
  if (typeof raw === 'string') {
    const m = raw.match(/^(\d+)\s*x\s*(\d+)$/i);
    if (m) {
      const w = Number(m[1]);
      const h = Number(m[2]);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) return { width: w, height: h };
    }
  }
  const lw = Number(source?.width);
  const lh = Number(source?.height);
  if (Number.isFinite(lw) && Number.isFinite(lh) && lw > 0 && lh > 0) return { width: lw, height: lh };
  return null;
}

type VideoTransformProps = VideoNodeProps & {
  title: string;
};

export const VideoTransform = memo(({
  data,
  id,
  type,
  title,
}: VideoTransformProps) => {
  const { t } = useI18n();
  const { updateNodeData, getNodes, getEdges, getNode, setEdges } = useReactFlow();
  const { extractToImageNode, extractToImageNodeAt } = useExtractFrame();
  const { getToken } = useAuth();
  const { user } = useUser();
  const [vidLoaded, setVidLoaded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // 未选中时用首帧图代替 <video>，避免每个视口内视频都挂载真实媒体（解码器/内存/带宽）。
  // 首帧图加载失败时回退到直接加载视频，保证旧节点不空白（见下方 useImageVariant 的 exhausted）。
  // IMPORTANT: 节点内视频与预览弹窗视频必须使用不同 ref，
  // 否则打开预览后 pause/play 会作用到错误的 video 元素，导致失焦后仍播放。
  const nodeVideoRef = useRef<HTMLVideoElement | null>(null);
  const inlineUploadRef = useRef<HTMLInputElement | null>(null);
  const [playingRefId, setPlayingRefId] = useState<string | null>(null);
  const refMediaEls = useRef<Map<string, HTMLVideoElement | HTMLAudioElement>>(new Map());
  const firstFrameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFrameInputRef = useRef<HTMLInputElement | null>(null);
  // Phase 9.6: removed `isHovered` React state (see image/transform.tsx note).
  // Hover state used to be a 1px inset visual feedback that triggered a full
  // node re-render on every mouse enter/leave — significant on canvases with
  // many video nodes during cursor movement.
  const isHovered = false;
  const setIsHovered = (() => {
    /* no-op: hover state removed for perf */
  }) as (v: boolean) => void;
  const [instructionsFocused, setInstructionsFocused] = useState(false);
  // precheck 缺失输入时的弹窗提醒（prompt / image / video / audio / alts），null 表示关闭。
  const [precheckMissing, setPrecheckMissing] = useState<string | null>(null);
  // 上游连了图片但选了文生视频模式时的提醒弹窗。
  const [t2vImageDialogOpen, setT2vImageDialogOpen] = useState(false);
  // 本地状态存储补充说明输入框的值，失去焦点后再保存
  const [localInstructions, setLocalInstructions] = useState((data as any)?.instructions ?? '');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const instructionInputRef = useRef<InstructionInputHandle>(null);
  const project = useProject();
  const { isSubscribed, modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels, consumeModelPass } = useSubscription();
  // 默认模型按「当前用户过滤后」的模型表解析：高优先级 default（如仅 international
  // 标签可见的国际版）不可见时，getDefaultModelId 自然回退到低优先级兜底。
  const accessFilteredVideoModels = useMemo(
    () => filterModelsClient(videoModels, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels),
    [modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels],
  );
  const modelId =
    data.model ??
    getPresetModelId('video', videoModels) ??
    getLastSelectedModelId('video', videoModels) ??
    getDefaultModelId(accessFilteredVideoModels);
  const selectedModel = videoModels[modelId];
  const isToolbarBlocked = useMemo(
    () => Boolean((data as any)?.status === "processing"),
    [(data as any)?.status]
  );
  const modelUi = (data as any)?.modelUi ?? {};
  // 与 image 节点一致：按 modelId 动态拉取高级选项（不依赖 video/index.ts 手动挂载）
  const uiOptions = useMemo<UiOption[]>(() => getUiOptionsForModel(modelId), [modelId]);
  const canUseAdvanced = isSubscribed;
  // 历史审核节点：generated.url 为 Asset:// 引用时用 assetPreviewUrl 播放/展示
  const assetPreviewUrl = (data as any)?.assetPreviewUrl as string | undefined;
  // 生成中状态：仅在 precheck 通过、真正开始保存/请求后才置 true，
  // 这样 precheck 失败弹窗时按钮不会闪一下转圈（与图像节点一致）。
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const hd = getHiddenDefaults(modelId);
    if (!Object.keys(hd).length) return;
    const missing = Object.keys(hd).filter(k => modelUi[k] === undefined);
    if (!missing.length) return;
    const patch: Record<string, any> = {};
    for (const k of missing) patch[k] = hd[k];
    updateNodeData(id, { modelUi: { ...modelUi, ...patch } });
  }, [modelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 同步 data.instructions 到本地状态（当外部更新时，但不在用户编辑时）
  useEffect(() => {
    // 如果用户正在编辑，不覆盖本地状态，避免打断输入
    if (!instructionsFocused) {
      setLocalInstructions((data as any)?.instructions ?? '');
    }
  }, [(data as any)?.instructions, instructionsFocused]);

  // 与 image 节点一致：当模型提供可选项且当前值非法时，进行约束修正；
  // 当前策略：当模型提供可选项且当前值为空/非法时，优先使用预设参数，否则写入默认值（第一个可用项），确保节点 data 落盘。
  useEffect(() => {
    const nextData: any = {};
    const cfg = getEffectiveConfig(selectedModel, (data as any)?.frameMode);
    const presetParams = getPresetModelParams(modelId);
    const allowedARs = cfg.aspectRatios;
    if (Array.isArray(allowedARs)) {
      const presetAR = (() => {
        if (presetParams?.aspectRatio && allowedARs.includes(presetParams.aspectRatio)) return presetParams.aspectRatio;
        try {
          const raw = getPresetAspectRatio();
          return raw ? String(raw).trim() : undefined;
        } catch {
          return undefined;
        }
      })();

      // aspectRatios: [] => Auto（清理残留的“比例设置”值，不写默认）。
      // 与 image 一致的策略：aspectRatio 只承载“比例设置”语义（如 "16:9"）；视频真实分辨率只存于
      // width/height，显示比例由 width/height 派生（见 surfaceAspectRatio）。因此这里可放心清理任何
      // 残留值（含历史上被误写入的分辨率字符串），且仅在当前确有值时才写，避免对已是空的字段空写。
      if (allowedARs.length === 0) {
        const curAR = (data as any)?.aspectRatio;
        if (curAR !== undefined && curAR !== null && curAR !== "") {
          nextData.aspectRatio = undefined;
        }
      } else {
        const isUnsetAR =
          typeof (data as any)?.aspectRatio === "undefined" ||
          (data as any)?.aspectRatio === null ||
          (data as any)?.aspectRatio === "" ||
          (data as any)?.aspectRatio === "Auto";
        if (isUnsetAR) {
          const pick = presetAR && allowedARs.includes(presetAR) ? presetAR : allowedARs[0];
          nextData.aspectRatio = pick;
        } else if (!allowedARs.includes(String((data as any)?.aspectRatio))) {
          const pick = presetAR && allowedARs.includes(presetAR) ? presetAR : allowedARs[0];
          nextData.aspectRatio = pick;
        }
      }
    }
    const allowedDurations = cfg.durations;
    if (Array.isArray(allowedDurations) && allowedDurations.length) {
      const curDur = Number((data as any)?.duration);
      const isUnsetDur =
        typeof (data as any)?.duration === "undefined" ||
        (data as any)?.duration === null ||
        (data as any)?.duration === "" ||
        (data as any)?.duration === "Auto";
      if (isUnsetDur) {
        const presetDur = presetParams?.duration != null ? Number(presetParams.duration) : undefined;
        nextData.duration = presetDur != null && allowedDurations.includes(presetDur) ? presetDur : allowedDurations[0];
      }
      else if (!Number.isFinite(curDur) || curDur <= 0) nextData.duration = allowedDurations[0];
      else if (!allowedDurations.includes(curDur)) nextData.duration = allowedDurations[0];
    }
    const allowedQualities = (() => {
      const map = cfg.qualityByDuration;
      const curDur = Number((nextData as any)?.duration ?? (data as any)?.duration);
      if (map && Number.isFinite(curDur) && map[curDur] && Array.isArray(map[curDur]) && map[curDur].length) {
        return map[curDur];
      }
      return cfg.qualities;
    })() as
      | string[]
      | undefined;
    if (Array.isArray(allowedQualities) && allowedQualities.length) {
      const curQ = (data as any)?.quality;
      const isUnsetQ =
        typeof curQ === "undefined" ||
        curQ === null ||
        curQ === "" ||
        curQ === "Auto";
      if (isUnsetQ) {
        const presetQ = presetParams?.quality;
        nextData.quality = presetQ && allowedQualities.includes(presetQ) ? presetQ : allowedQualities[0];
      }
      else if (typeof curQ !== "string" || !allowedQualities.includes(curQ)) nextData.quality = allowedQualities[0];
    }
    const curFrameMode = (data as any)?.frameMode as string | undefined;
    const si = Array.isArray((selectedModel as any)?.supportsInput) ? (selectedModel as any).supportsInput as string[] : [];
    const validModes = new Set<string>();
    if (si.includes('t2v')) validModes.add('none');
    if (si.includes('first') || si.includes('i2v')) validModes.add('first');
    if (si.includes('first_last')) validModes.add('first_last');
    if (si.includes('reference')) validModes.add('ref');
    if (si.includes('avatar')) validModes.add('avatar');
    if (si.includes('v2v')) validModes.add('v2v');
    if (curFrameMode && !validModes.has(curFrameMode)) {
      nextData.frameMode = validModes.has('first') ? 'first' : [...validModes][0];
    } else if (!curFrameMode && validModes.size > 0) {
      nextData.frameMode = validModes.has('first') ? 'first' : [...validModes][0];
    }
    if (Object.keys(nextData).length) updateNodeData(id, nextData);
  }, [
    id,
    selectedModel,
    updateNodeData,
    (data as any)?.aspectRatio,
    (data as any)?.duration,
    (data as any)?.quality,
    (data as any)?.frameMode,
  ]);

  // 防止节点运行中浏览器意外刷新/关闭
  useBeforeUnload((data as any)?.status === "processing");

  // 生成进度：优先使用服务端按真实完成记录计算的缓存 P90，未就绪时回退到默认值。
  const { progress, remainingText, hasEstimate } = useLinearProgress({
    isProcessing: (data as any)?.status === "processing",
    updatedAt: (data as any)?.updatedAt,
    runtimeEstimatedTime: Number((data as any)?.runtimeEstimatedTime),
    fallbackSeconds: 10 * 60,
  });

  // 回填：节点初次创建且未设置 model 时，将选中的默认模型写回节点数据
  useEffect(() => {
    try {
      if (!data.model && modelId) {
        updateNodeData(id, { model: modelId });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modelId, data.model]);

  // 订阅当前节点的连接，而不是在每次 React Flow store 更新时扫描全量 nodes/edges。
  const incomingConnections = useNodeConnections({ id, handleType: "target" });
  const incomers = useMemo(
    () =>
      incomingConnections
        .map((connection: any) => getNode(String(connection?.source || "")))
        .filter(Boolean),
    [incomingConnections, getNode],
  );
  const incomingEdges = useMemo(() => {
    const edges = getEdges?.() || [];
    const edgeById = new Map(edges.map((edge: any) => [String(edge?.id), edge]));
    return incomingConnections
      .map((connection: any) => {
        if (connection?.edgeId) {
          return edgeById.get(String(connection.edgeId));
        }
        return edges.find(
          (edge: any) =>
            String(edge?.source) === String(connection?.source) &&
            String(edge?.target) === String(id),
        );
      })
      .filter(Boolean);
  }, [getEdges, id, incomingConnections]);

  // 获取模型能力描述和提醒信息（基于 incomers）
  const modelCapabilities = useVideoGuidance({
    selectedModel: selectedModel as any,
    data,
    incomers: incomers as any,
    t,
    modelId,
  });

  // 快捷操作：添加提示词节点、图像节点和视频节点
  const { handleAddImageNode, handleAddVideoNode } = useVideoGuidanceActions({ id, t });

  // 从 modelCapabilities 中获取节点状态（避免重复计算）
  const { hasIncomingVideoNodes } = modelCapabilities;

  const incomersForOptions = incomers as any[];

  // 预估费用（用于节点底部工具栏的积分显示）：与侧边栏共用同一套逻辑
  const { usdCost: estimatedUsdCost, passCount: rawPassCount } = useMemo(
    () =>
      estimateVideoBilling({
        model: selectedModel as any,
        nodeData: data as any,
        upstreamNodes: incomersForOptions as any,
      }),
    [
      selectedModel,
      incomersForOptions,
      (data as any)?.durationSeconds,
      (data as any)?.generated?.durationSeconds,
      (data as any)?.duration,
      (data as any)?.quality,
      (data as any)?.resolution,
      (data as any)?.modelUi,
      (data as any)?.inlineAttachments,
    ]
  );
  // passCount 为折后次卡数量（模型卡折扣），仅用于展示/预估；请求侧仍由服务端按原始数量打折实扣。
  const { credits: estimatedCredits, passRemaining, discountMultiplier, passCount, passDiscountMultiplier } = useEstimatedBilling(estimatedUsdCost, modelId, rawPassCount);

  const { incomingImageInputs, focusNodeById, isTargetSelected } =
    useIncomingImageInputs({
      id,
      t,
      includeFrameRole: true,
    });

  const inlineAttachments = useMemo<InlineAttachment[]>(
    () => Array.isArray((data as any)?.inlineAttachments) ? (data as any).inlineAttachments : [],
    [(data as any)?.inlineAttachments]
  );
  const handleInlineAttachmentsChange = useCallback(
    (next: InlineAttachment[], removed?: InlineAttachment) => {
      if (removed?.role === 'start') {
        const imgs = next.filter(a => a.fileType === 'image');
        const endImg = imgs.find(a => a.role === 'end');
        if (endImg) {
          next = next.map(a => a.id === endImg.id ? { ...a, role: 'start' as const } : a);
        } else if (imgs.length > 0) {
          const first = imgs[0];
          next = next.map(a => a.id === first.id ? { ...a, role: 'start' as const } : a);
        }
      }
      updateNodeData(id, { inlineAttachments: next });
    },
    [id, updateNodeData]
  );

  const connectedCounts = useMemo(() => {
    const c = { image: 0, video: 0, audio: 0 };
    for (const n of incomers as any[]) {
      const mt = classifyNodeMediaType(n);
      if (mt) c[mt]++;
    }
    return c;
  }, [incomers]);

  const currentFrameMode = (data as any)?.frameMode as string | undefined;
  const ec = useMemo(() => getEffectiveConfig(selectedModel, currentFrameMode), [selectedModel, currentFrameMode]);
  const effectiveInlineMedia = useMemo(() => ec.inlineMedia ?? defaultInlineMediaForMode(currentFrameMode), [ec.inlineMedia, currentFrameMode]);

  const incomingVideoAudioInputs = useMemo(() => {
    const isRefLike = currentFrameMode === 'ref' || currentFrameMode === 'avatar';
    const vidAllowed = effectiveInlineMedia.video?.allow !== false;
    const audAllowed = effectiveInlineMedia.audio?.allow !== false;
    const vidMax = !vidAllowed ? 0 : isRefLike ? Infinity : (effectiveInlineMedia.video?.maxCount ?? Infinity);
    const audMax = !audAllowed ? 0 : isRefLike ? Infinity : (effectiveInlineMedia.audio?.maxCount ?? Infinity);
    const mediaMax = isRefLike ? Infinity : (effectiveInlineMedia.maxMediaCount ?? Infinity);
    const items: { sourceId: string; url?: string; label: string; mediaType: 'video' | 'audio'; idx: number }[] = [];
    let vidContentIdx = 0;
    let audContentIdx = 0;
    let vidSlot = 0;
    let audSlot = 0;
    let mediaTotal = 0;
    for (const n of incomers as any[]) {
      const mediaType = classifyNodeMediaType(n);
      if (mediaType !== 'video' && mediaType !== 'audio') continue;
      const d = n.data as any;
      const rawUrl: string | undefined = (n.type === 'video' || n.type === 'audio')
        ? (d?.generated?.url || d?.content?.url)
        : d?.content?.url;
      // 上游已提交审核（Asset:// 引用）时用其预览 URL 展示
      const url = resolveAssetRefPreview(rawUrl, d?.assetPreviewUrl) ?? rawUrl;
      const label = d?.name || d?.content?.name || t(mediaType === 'video' ? 'Video Node' : 'Audio');
      if (mediaTotal >= mediaMax) continue;
      if (mediaType === 'video' && vidSlot >= vidMax) continue;
      if (mediaType === 'audio' && audSlot >= audMax) continue;
      mediaTotal++;
      if (mediaType === 'video') { vidSlot++; if (url) vidContentIdx++; items.push({ sourceId: n.id, url, label, mediaType, idx: url ? vidContentIdx : 0 }); }
      else { audSlot++; if (url) audContentIdx++; items.push({ sourceId: n.id, url, label, mediaType, idx: url ? audContentIdx : 0 }); }
    }
    return items;
  }, [incomers, t, effectiveInlineMedia, currentFrameMode]);

  // ── Auto-sync orderKeys（连接/断开/添加/删除时自动维护，拖拽时由用户直接设置）──
  useEffect(() => {
    const upImageRids = incomingImageInputs.map(img => img.rid);
    const upVideoRids = incomingVideoAudioInputs.filter(va => va.mediaType === 'video').map(va => `up:${va.sourceId}`);
    const upAudioRids = incomingVideoAudioInputs.filter(va => va.mediaType === 'audio').map(va => `up:${va.sourceId}`);
    const inImageRids = inlineAttachments.filter(a => a.fileType === 'image').map(a => `in:${a.id}`);
    const inVideoRids = inlineAttachments.filter(a => a.fileType === 'video').map(a => `in:${a.id}`);
    const inAudioRids = inlineAttachments.filter(a => a.fileType === 'audio').map(a => `in:${a.id}`);

    const frameRids = [...upImageRids, ...inImageRids];
    const refRids = [...upImageRids, ...upVideoRids, ...upAudioRids, ...inImageRids, ...inVideoRids, ...inAudioRids];
    const videoRids = [...upVideoRids, ...inVideoRids];
    const audioRids = [...upAudioRids, ...inAudioRids];

    const syncOrder = (saved: string[] | undefined, rids: string[]): string[] | null => {
      const ridSet = new Set(rids);
      if (!saved) return rids.length > 0 ? rids : null;
      const kept = saved.filter(r => ridSet.has(r));
      const existing = new Set(kept);
      const added = rids.filter(r => !existing.has(r));
      const result = [...kept, ...added];
      if (result.length === saved.length && result.every((r, i) => r === saved[i])) return null;
      return result;
    };

    const hasLegacyRoles = !!(data as any)?.edgeFrameRoles;
    // Perf/正确性：incomingImageInputs 仅在节点“选中”时才非空（性能门控）。
    // 未选中时 upImageRids 为空，若仍同步 frame/referenceImageOrder 会把图片项
    // 误判为“已移除”从而清空——既丢失用户自定义排序，又造成每次选中/取消选中
    // 都改写节点 data → 重渲染 + 标脏 + 保存（拖拽改变选中态时同样触发）。
    // 因此 image 顺序仅在选中时同步；video/audio 顺序不含 image rid，可照常。
    const newFrame = !isTargetSelected
      ? null
      : (!((data as any)?.frameImageOrder) && hasLegacyRoles)
        ? null
        : syncOrder((data as any)?.frameImageOrder, frameRids);
    const newRef = !isTargetSelected
      ? null
      : syncOrder((data as any)?.referenceImageOrder, refRids);
    const newVideo = syncOrder((data as any)?.videoOrder, videoRids);
    const newAudio = syncOrder((data as any)?.audioOrder, audioRids);

    const patch: Record<string, any> = {};
    if (newFrame !== null) patch.frameImageOrder = newFrame;
    if (newRef !== null) patch.referenceImageOrder = newRef;
    if (newVideo !== null) patch.videoOrder = newVideo;
    if (newAudio !== null) patch.audioOrder = newAudio;

    if (Object.keys(patch).length > 0) updateNodeData(id, patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingImageInputs, inlineAttachments, incomingVideoAudioInputs, id, updateNodeData, isTargetSelected]);

  // Re-validate incomers when upstream nodes get new content
  const incomerDurKey = useMemo(() =>
    (incomers as any[]).map((n: any) => `${n.id}:${(n.data as any)?.durationSeconds ?? ''}`).join(','),
    [incomers]
  );
  const prevInlineMediaRef = useRef(effectiveInlineMedia);
  useEffect(() => {
    // 模式/模型切换导致 config 变化时，跳过验证（不断边、不弹 toast），仅更新 ref
    if (prevInlineMediaRef.current !== effectiveInlineMedia) {
      prevInlineMediaRef.current = effectiveInlineMedia;
      return;
    }

    const edgesToRemove: string[] = [];

    const inlineCounts = countByType(inlineAttachments);
    let totalMediaDur = 0;
    for (const att of inlineAttachments) {
      if (att.duration && (att.fileType === 'video' || att.fileType === 'audio')) {
        totalMediaDur += Number(att.duration) || 0;
      }
    }

    const incomerList = incomers as any[];
    const incEdges = incomingEdges as any[];
    let totalAll = inlineCounts.image + inlineCounts.video + inlineCounts.audio;
    let mediaAll = inlineCounts.video + inlineCounts.audio;
    const typeSlots: Record<string, number> = { image: inlineCounts.image, video: inlineCounts.video, audio: inlineCounts.audio };

    const candidatesAllowed = effectiveInlineMedia.allowCandidates !== false;

    for (const n of incomerList) {
      const ct = classifyNodeMediaType(n);
      if (!ct) continue;
      const typeCfg = effectiveInlineMedia[ct];
      const findEdge = () => incEdges.find((e: any) => String(e?.source) === String(n.id));

      // allow: false → 跳过（不断边、不弹 toast）；新边的 allow 拦截由 canvas.tsx 处理
      if (!typeCfg || !typeCfg.allow) continue;

      // 2. 检查是否超限
      const exceedsType = (typeSlots[ct] ?? 0) >= (typeCfg.maxCount ?? Infinity);
      const exceedsMedia = ct !== 'image' && effectiveInlineMedia.maxMediaCount != null && mediaAll >= effectiveInlineMedia.maxMediaCount;
      const exceedsTotal = effectiveInlineMedia.maxTotalCount != null && totalAll >= effectiveInlineMedia.maxTotalCount;

      if (exceedsType || exceedsMedia || exceedsTotal) {
        if (candidatesAllowed) {
          totalAll++;
          if (ct !== 'image') mediaAll++;
          typeSlots[ct] = (typeSlots[ct] ?? 0) + 1;
        } else {
          const edge = findEdge();
          if (edge) {
            const msg = exceedsType
              ? String(t('Maximum {{count}} {{type}} allowed', { count: typeCfg.maxCount, type: t(ct) }))
              : exceedsMedia
                ? String(t('Maximum {{count}} video/audio files allowed', { count: effectiveInlineMedia.maxMediaCount }))
                : String(t('Maximum {{count}} files allowed in total', { count: effectiveInlineMedia.maxTotalCount }));
            edgesToRemove.push(edge.id);
            toast.error(msg);
          }
        }
        continue;
      }

      // 3. 未超限：校验媒体属性（时长/尺寸等）
      const dur = Number((n.data as any)?.durationSeconds) || 0;
      if (dur > 0) {
        const dims = parseVideoDimensions(n.data);
        const w = dims?.width ?? 0;
        const h = dims?.height ?? 0;
        const err = validateMediaItem(typeCfg, effectiveInlineMedia, ct, { duration: dur, width: w, height: h }, totalMediaDur);
        if (err) {
          const edge = findEdge();
          if (edge) { edgesToRemove.push(edge.id); toast.error(String(t(err.key, err.params))); }
          continue;
        }
        totalMediaDur += dur;
      }

      // 4. 通过所有检查，计数
      totalAll++;
      if (ct !== 'image') mediaAll++;
      typeSlots[ct] = (typeSlots[ct] ?? 0) + 1;
    }

    if (edgesToRemove.length > 0) {
      const removeSet = new Set(edgesToRemove);
      setEdges((eds) => eds.filter((e) => !removeSet.has(e.id)));
    }
  }, [incomerDurKey, effectiveInlineMedia, inlineAttachments, incomingEdges, t, setEdges]);

  // ── Frame Mode selector ────────────────────────────────────────
  const supportsInput = selectedModel?.supportsInput as string[] | undefined;
  const selectedModelTags = Array.isArray((selectedModel as any)?.tags) ? ((selectedModel as any).tags as string[]) : [];
  const isVideoUpscaleModel = selectedModelTags.includes('video-upscale');
  const frameModeLabels = (selectedModel as any)?.frameModeLabels as Record<string, string> | undefined;
  const frameModeOptions = useMemo(() => {
    const si = Array.isArray(supportsInput) ? supportsInput : [];
    const label = (mode: string, defaultKey: string) => t(frameModeLabels?.[mode] ?? defaultKey);
    const opts: { value: string; label: string }[] = [];
    if (si.includes('t2v')) opts.push({ value: 'none', label: label('none', 'Text to Video') });
    if (si.includes('first') || si.includes('i2v')) opts.push({ value: 'first', label: label('first', 'First Frame') });
    if (si.includes('first_last')) opts.push({ value: 'first_last', label: label('first_last', 'First & Last') });
    if (si.includes('reference')) opts.push({ value: 'ref', label: label('ref', 'Reference') });
    if (si.includes('avatar')) opts.push({ value: 'avatar', label: label('avatar', 'Avatar') });
    if (si.includes('v2v')) opts.push({ value: 'v2v', label: isVideoUpscaleModel ? t('Video Super Resolution') : label('v2v', 'video2video') });
    return opts;
  }, [supportsInput, isVideoUpscaleModel, frameModeLabels, t]);

  const handleFrameModeChange = useCallback(
    (mode: string) => {
      updateNodeData(id, { frameMode: mode });

      // Sync inline attachment roles
      if (inlineAttachments.some((a) => a.fileType === 'image')) {
        if (mode === 'ref' || mode === 'avatar') {
          handleInlineAttachmentsChange(
            inlineAttachments.map((a) => (a.fileType === 'image' ? { ...a, role: 'reference' as const } : a))
          );
        } else if (mode === 'first_last') {
          const hasStart = inlineAttachments.some((a) => a.fileType === 'image' && a.role === 'start');
          const hasEnd = inlineAttachments.some((a) => a.fileType === 'image' && a.role === 'end');
          let needStart = !hasStart;
          let needEnd = !hasEnd;
          handleInlineAttachmentsChange(
            inlineAttachments.map((a) => {
              if (a.fileType !== 'image') return a;
              if (a.role === 'start') return a;
              if (a.role === 'end') return a;
              if (needStart) { needStart = false; return { ...a, role: 'start' as const }; }
              if (needEnd) { needEnd = false; return { ...a, role: 'end' as const }; }
              return { ...a, role: undefined };
            })
          );
        } else if (mode === 'first') {
          const hasStart = inlineAttachments.some((a) => a.fileType === 'image' && a.role === 'start');
          let needStart = !hasStart;
          handleInlineAttachmentsChange(
            inlineAttachments.map((a) => {
              if (a.fileType !== 'image') return a;
              if (a.role === 'start') return a;
              if (needStart) { needStart = false; return { ...a, role: 'start' as const }; }
              return { ...a, role: undefined };
            })
          );
        }
      }

    },
    [id, updateNodeData, inlineAttachments, handleInlineAttachmentsChange]
  );


  // ── Drag-and-drop sensors (shared) ─────────────────────────────
  const inlineSortSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // ── Inline-only reorder (video/audio grid) ────────────────────
  const handleInlineReorder = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = inlineAttachments.findIndex((a) => a.id === active.id);
      const newIdx = inlineAttachments.findIndex((a) => a.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      handleInlineAttachmentsChange(arrayMove(inlineAttachments, oldIdx, newIdx));
    },
    [inlineAttachments, handleInlineAttachmentsChange]
  );

  // ── V2V mode: video items ──────────────────────────────────────
  const v2vItems = useMemo(() => {
    if (currentFrameMode !== 'v2v') return [];
    const items: { id: string; source: 'upstream' | 'inline'; url?: string; name?: string; sourceId?: string; attId?: string }[] = [];
    for (const va of incomingVideoAudioInputs) {
      if (va.mediaType === 'video') {
        items.push({ id: `up:${va.sourceId}`, source: 'upstream', url: va.url, name: va.label, sourceId: va.sourceId });
      }
    }
    for (const att of inlineAttachments) {
      if (att.fileType === 'video' && effectiveInlineMedia.video?.allow !== false) {
        items.push({ id: `in:${att.id}`, source: 'inline', url: att.url, name: att.name, attId: att.id });
      }
    }
    return items;
  }, [currentFrameMode, incomingVideoAudioInputs, inlineAttachments, effectiveInlineMedia]);

  // v2v 模式下的参考图像（上游图像节点 + 内联图片附件）。v2v 渲染分支独立于
  // ref/avatar 的 DraggableMediaGrid，需要单独收集图像才能在工具栏中展示与上传。
  const v2vImageItems = useMemo(() => {
    if (currentFrameMode !== 'v2v') return [];
    if (effectiveInlineMedia.image?.allow !== true) return [];
    const items: { id: string; source: 'upstream' | 'inline'; url?: string; previewUrl?: string; sourceId?: string; attId?: string }[] = [];
    for (const img of incomingImageInputs) {
      items.push({ id: img.rid, source: 'upstream', url: img.url, sourceId: img.sourceId });
    }
    for (const att of inlineAttachments) {
      if (att.fileType === 'image') {
        items.push({ id: `in:${att.id}`, source: 'inline', url: att.url, previewUrl: att.previewUrl, attId: att.id });
      }
    }
    return items;
  }, [currentFrameMode, incomingImageInputs, inlineAttachments, effectiveInlineMedia]);

  // ── Reference mode: unified ordered list ───────────────────────
  type RefMediaItem = {
    id: string;
    source: 'upstream' | 'inline';
    url?: string;
    name?: string;
    sourceId?: string;
    attId?: string;
    mediaType: 'image' | 'video' | 'audio';
    previewUrl?: string;
  };

  const referenceImageOrder = (data as any)?.referenceImageOrder as string[] | undefined;

  const orderedReferenceImages = useMemo((): RefMediaItem[] => {
    if (currentFrameMode !== 'ref' && currentFrameMode !== 'avatar') return [];
    const mediaMap = new Map<string, RefMediaItem>();
    if (effectiveInlineMedia.image?.allow !== false) {
      for (const img of incomingImageInputs) {
        mediaMap.set(img.rid, {
          id: img.rid, source: 'upstream',
          url: img.url, name: img.label, sourceId: img.sourceId, mediaType: 'image',
        });
      }
    }
    for (const va of incomingVideoAudioInputs) {
      mediaMap.set(`up:${va.sourceId}`, {
        id: `up:${va.sourceId}`, source: 'upstream',
        url: va.url, name: va.label, sourceId: va.sourceId, mediaType: va.mediaType,
      });
    }
    for (const att of inlineAttachments) {
      if (att.fileType !== 'image' && att.fileType !== 'video' && att.fileType !== 'audio') continue;
      if (effectiveInlineMedia[att.fileType as 'image' | 'video' | 'audio']?.allow === false) continue;
      mediaMap.set(`in:${att.id}`, {
        id: `in:${att.id}`, source: 'inline',
        url: att.url, name: att.name, attId: att.id, mediaType: att.fileType as 'image' | 'video' | 'audio', previewUrl: att.previewUrl,
      });
    }
    const ordered: RefMediaItem[] = [];
    const seen = new Set<string>();
    if (Array.isArray(referenceImageOrder)) {
      for (const rid of referenceImageOrder) {
        const item = mediaMap.get(rid);
        if (item && !seen.has(rid)) { seen.add(rid); ordered.push(item); }
      }
    }
    for (const [rid, item] of mediaMap) {
      if (!seen.has(rid)) ordered.push(item);
    }
    return ordered;
  }, [currentFrameMode, incomingImageInputs, incomingVideoAudioInputs, inlineAttachments, referenceImageOrder, effectiveInlineMedia]);

  const refActiveIds = useMemo(() => {
    if (currentFrameMode !== 'ref' && currentFrameMode !== 'avatar') return new Set<string>();
    const im = effectiveInlineMedia;
    const maxByType: Record<string, number> = {
      image: (im.image?.allow !== false) ? (im.image?.maxCount ?? Infinity) : 0,
      video: (im.video?.allow !== false) ? (im.video?.maxCount ?? Infinity) : 0,
      audio: (im.audio?.allow !== false) ? (im.audio?.maxCount ?? Infinity) : 0,
    };
    const maxMedia = im.maxMediaCount ?? Infinity;
    const maxTotal = im.maxTotalCount ?? Infinity;
    const ids = new Set<string>();
    const typeCounts: Record<string, number> = { image: 0, video: 0, audio: 0 };
    let mediaUsed = 0;
    let totalUsed = 0;
    for (const ri of orderedReferenceImages) {
      const mt = ri.mediaType ?? 'image';
      const withinType = (typeCounts[mt] ?? 0) < (maxByType[mt] ?? Infinity);
      const withinMedia = mt === 'image' || mediaUsed < maxMedia;
      if (withinType && withinMedia && totalUsed < maxTotal) {
        ids.add(ri.id);
        typeCounts[mt] = (typeCounts[mt] ?? 0) + 1;
        if (mt !== 'image') mediaUsed++;
        totalUsed++;
      }
    }
    return ids;
  }, [currentFrameMode, orderedReferenceImages, effectiveInlineMedia]);

  const displayRefImages = useMemo(() => {
    const active: RefMediaItem[] = [];
    const candidates: RefMediaItem[] = [];
    for (const item of orderedReferenceImages) {
      (refActiveIds.has(item.id) ? active : candidates).push(item);
    }
    return [...active, ...candidates];
  }, [orderedReferenceImages, refActiveIds]);

  const refItemLabels = useMemo(() => {
    const labels = new Map<string, string>();
    const counts: Record<string, number> = {};
    for (const item of displayRefImages) {
      const suffix = item.name ? `（${item.name}）` : '';
      if (refActiveIds.has(item.id)) {
        const mt = item.mediaType ?? 'image';
        counts[mt] = (counts[mt] ?? 0) + 1;
        const typeLabel = mt === 'image' ? t('Image') : mt === 'video' ? t('Video') : t('Audio');
        labels.set(item.id, `${typeLabel} ${counts[mt]}${suffix}`);
      } else {
        labels.set(item.id, `${t('Candidate')}${suffix}`);
      }
    }
    return labels;
  }, [displayRefImages, refActiveIds, t]);

  const mentionLabels = useMemo<MentionLabels>(
    () => ({ image: t('Image'), video: t('Video'), audio: t('Audio') }),
    [t],
  );

  const commitInstructionsToStore = useCallback(
    (text: string) => updateNodeData(id, { instructions: text }),
    [id, updateNodeData],
  );

  const deferredRewrite = useDeferredInstructionsRewrite(
    localInstructions,
    setLocalInstructions,
    commitInstructionsToStore,
  );

  const handleReferenceReorder = useCallback(
    (event: DragEndEvent) => {
      const { active: dragActive, over } = event;
      if (!over || dragActive.id === over.id) return;
      const ids = displayRefImages.map((item) => item.id);
      const oldIdx = ids.indexOf(String(dragActive.id));
      const newIdx = ids.indexOf(String(over.id));
      if (oldIdx === -1 || newIdx === -1) return;
      const newOrder = arrayMove(ids, oldIdx, newIdx);

      // 关键：先以最小工作量同步落地 referenceImageOrder（与原实现一致），保证 dnd-kit 在
      // onDragEnd 时立刻看到新顺序，避免拖放时出现"甩一下再归位"。
      updateNodeData(id, { referenceImageOrder: newOrder });

      const baseline = deferredRewrite.getBaselineText();
      if (!baseline) return;
      // 与 ref/avatar 模式 allImageInputsForMention 计算口径一致：按 mediaType 各自计数。
      type Mt = 'image' | 'video' | 'audio';
      const buildInputs = (items: typeof displayRefImages) => {
        const counts: Record<Mt, number> = { image: 0, video: 0, audio: 0 };
        const out: Array<{ idx: number; rid: string; sourceId: string; label: string; mediaType: Mt }> = [];
        for (const it of items) {
          if (!refActiveIds.has(it.id) || !it.url) continue;
          const mt = (it.mediaType ?? 'image') as Mt;
          counts[mt]++;
          out.push({ idx: counts[mt], rid: it.id, sourceId: '', label: '', mediaType: mt });
        }
        return out;
      };
      const reordered = arrayMove(displayRefImages, oldIdx, newIdx);
      const oldInputs = buildInputs(displayRefImages);
      const newInputs = buildInputs(reordered);
      const nextText = rewriteMentionsAfterReorder(baseline, oldInputs as any, newInputs as any, mentionLabels);
      if (nextText === baseline) return;
      // 推迟到下一帧：避免与 dnd-kit drop 过渡动画抢同一帧导致"甩一下"。
      deferredRewrite.schedule(nextText);
    },
    [displayRefImages, refActiveIds, mentionLabels, id, updateNodeData, deferredRewrite]
  );

  // ── First / First&Last mode: ordered image list ─────────────────
  type FrameImg = { key: string; src: 'upstream' | 'inline'; url: string; id?: string; sourceId?: string; role?: string };
  const frameRoleSlots: ('start' | 'end')[] = useMemo(
    () => currentFrameMode === 'first' ? ['start'] : currentFrameMode === 'first_last' ? ['start', 'end'] : [],
    [currentFrameMode]
  );

  const frameImages = useMemo((): FrameImg[] => {
    if (currentFrameMode !== 'first' && currentFrameMode !== 'first_last') return [];
    const all: FrameImg[] = [];
    const seen = new Set<string>();
    for (const it of incomingImageInputs) {
      if (!it.url) continue;
      const k = it.rid;
      if (seen.has(k)) continue;
      seen.add(k);
      all.push({ key: k, src: 'upstream', url: it.url, sourceId: it.sourceId, role: it.role });
    }
    for (const a of inlineAttachments) {
      if (a.fileType !== 'image' || !a.url) continue;
      const k = `in:${a.id}`;
      if (seen.has(k)) continue;
      seen.add(k);
      all.push({ key: k, src: 'inline', url: a.url, id: a.id, role: a.role });
    }
    const savedOrder = (data as any)?.frameImageOrder as string[] | undefined;
    if (Array.isArray(savedOrder) && savedOrder.length > 0) {
      const orderMap = new Map(savedOrder.map((k, i) => [k, i]));
      all.sort((a, b) => (orderMap.get(a.key) ?? 999) - (orderMap.get(b.key) ?? 999));
    } else {
      all.sort((a, b) => {
        const ai = a.role ? frameRoleSlots.indexOf(a.role as any) : -1;
        const bi = b.role ? frameRoleSlots.indexOf(b.role as any) : -1;
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return 0;
      });
    }
    return all;
  }, [currentFrameMode, incomingImageInputs, inlineAttachments, (data as any)?.frameImageOrder, frameRoleSlots]);

  const handleFrameImageReorder = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = frameImages.map((i) => i.key);
      const oldIdx = ids.indexOf(String(active.id));
      const newIdx = ids.indexOf(String(over.id));
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(frameImages, oldIdx, newIdx);

      const roleByKey = new Map<string, 'start' | 'end' | undefined>();
      reordered.forEach((ri, i) => {
        roleByKey.set(ri.key, i < frameRoleSlots.length ? frameRoleSlots[i] : undefined);
      });

      updateNodeData(id, { frameImageOrder: reordered.map((ri) => ri.key) });

      handleInlineAttachmentsChange(
        inlineAttachments.map((a) => {
          if (a.fileType !== 'image') return a;
          const k = `in:${a.id}`;
          return roleByKey.has(k) ? { ...a, role: roleByKey.get(k) } : a;
        })
      );
    },
    [frameImages, frameRoleSlots, id, updateNodeData, inlineAttachments, handleInlineAttachmentsChange]
  );

  const [inlineUploading, setInlineUploading] = useState(false);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const subjectPickerTargetRoleRef = useRef<'start' | 'end' | 'reference' | undefined>(undefined);
  const assetPickerTargetRoleRef = useRef<'start' | 'end' | 'reference' | undefined>(undefined);
  const openAssetPicker = useCallback((ft?: 'image' | 'video' | 'audio', role?: 'start' | 'end' | 'reference') => {
    assetPickerTargetRoleRef.current = role;
    void requestLibraryPick({ fileType: ft }).then((item) => {
      if (item) void handlePickFromLibrary(item);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [frameSlotUploading, setFrameSlotUploading] = useState<'start' | 'end' | null>(null);
  const handleInlineUploadFiles = useCallback(async (files: FileList | null) => {
    if (!files?.length || !user?.id || !project?.id) return;
    setInlineUploading(true);
    try {
      const token = await getToken({ template: 'supabase' });
      if (!token) throw new Error('No auth token');
      const latest = (getNode(id) as any)?.data;
      const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];
      const curCounts = countByType(cur);

      const added: InlineAttachment[] = [];
      const addedCounts = { image: 0, video: 0, audio: 0 };
      let addedMediaDur = 0;
      const existingMediaDur = cur.reduce((s, a) => s + (a.duration ?? 0), 0);

      for (const file of Array.from(files)) {
        const fileType = detectMediaType(file.type);
        let fileDuration = 0;

        {
          const typeCfg = effectiveInlineMedia[fileType];
          if (!typeCfg?.allow) {
            toast.error(t('This model does not support {{type}} inline upload', { type: t(fileType) }));
            continue;
          }
          const cc = connectedCounts;
          const totalAll = (cc.image + cc.video + cc.audio) + cur.length + added.length;
          if (effectiveInlineMedia.maxTotalCount != null && totalAll >= effectiveInlineMedia.maxTotalCount) {
            toast.error(t('Maximum {{count}} files allowed in total', { count: effectiveInlineMedia.maxTotalCount }));
            continue;
          }
          if (fileType !== 'image' && effectiveInlineMedia.maxMediaCount != null) {
            const mediaAll = (cc.video + cc.audio) + (curCounts.video + curCounts.audio) + addedCounts.video + addedCounts.audio;
            if (mediaAll >= effectiveInlineMedia.maxMediaCount) {
              toast.error(t('Maximum {{count}} video/audio files allowed', { count: effectiveInlineMedia.maxMediaCount }));
              continue;
            }
          }
          const typeTotal = (cc[fileType] ?? 0) + (curCounts[fileType] ?? 0) + addedCounts[fileType];
          if (typeTotal >= (typeCfg.maxCount ?? Infinity)) {
            toast.error(t('Maximum {{count}} {{type}} allowed', { count: typeCfg.maxCount, type: t(fileType) }));
            continue;
          }
          if (typeCfg.formats?.length) {
            const mime = file.type.toLowerCase();
            if (!typeCfg.formats.some(f => f === mime || (f.endsWith('/*') && mime.startsWith(f.slice(0, -1))))) {
              toast.error(t('Unsupported format {{format}}', { format: file.type || file.name.split('.').pop() }));
              continue;
            }
          }
          if (file.size > parseBytes(typeCfg.maxSize)) {
            toast.error(t('File exceeds size limit of {{size}}', { size: typeCfg.maxSize }));
            continue;
          }
          if (fileType !== 'image') {
            fileDuration = await getMediaDuration(file);
          }
          const dims = (fileType === 'video' && typeCfg.maxPixels != null) ? await getVideoPixels(file) : null;
          const mediaErr = validateMediaItem(typeCfg, effectiveInlineMedia, fileType, {
            duration: fileDuration, width: dims?.width, height: dims?.height,
          }, existingMediaDur + addedMediaDur);
          if (mediaErr) {
            toast.error(String(t(mediaErr.key, mediaErr.params)));
            continue;
          }
        }

        const { url, type } = await uploadFile(file, 'files', {
          token, userId: user.id, projectId: String(project.id), assetType: fileType,
        });
        added.push({ id: nanoid(), url, type, fileType, name: file.name, duration: fileDuration || undefined });
        addedCounts[fileType]++;
        addedMediaDur += fileDuration;
      }
      if (added.length > 0) {
        const freshData = (getNode(id) as any)?.data;
        const freshCur = Array.isArray(freshData?.inlineAttachments) ? freshData.inlineAttachments as InlineAttachment[] : [];

        const newImages = added.filter(a => a.fileType === 'image');
        if (newImages.length > 0) {
          const explicitMode = (freshData?.frameMode as string | undefined) ?? 'none';
          const existingImgs = freshCur.filter(a => a.fileType === 'image');
          let hasStart = existingImgs.some(a => a.role === 'start');
          let hasEnd = existingImgs.some(a => a.role === 'end');
          for (const img of newImages) {
            if (explicitMode === 'ref' || explicitMode === 'avatar') {
              img.role = 'reference';
            } else if (explicitMode === 'first_last') {
              if (!hasStart) { img.role = 'start'; hasStart = true; }
              else if (!hasEnd) { img.role = 'end'; hasEnd = true; }
            } else if (explicitMode === 'first') {
              if (!hasStart) { img.role = 'start'; hasStart = true; }
            }
          }
        }

        updateNodeData(id, { inlineAttachments: [...freshCur, ...added].slice(0, 16) });
      }
    } catch (error) {
      handleError(t('Upload failed'), error);
    } finally {
      setInlineUploading(false);
      if (inlineUploadRef.current) inlineUploadRef.current.value = '';
    }
  }, [user?.id, project?.id, getToken, getNode, id, updateNodeData, t, effectiveInlineMedia, connectedCounts]);

  const handlePickFromLibrary = useCallback(async (item: LibraryPickResult) => {
    const latest = (getNode(id) as any)?.data;
    const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];
    const curCounts = countByType(cur);
    const fileType = item.type || 'image';
    const typeCfg = effectiveInlineMedia[fileType as 'image' | 'video' | 'audio'];
    if (!typeCfg?.allow) {
      toast.error(t('This model does not support {{type}} inline upload', { type: t(fileType) }));
      return;
    }
    const cc = connectedCounts;
    const totalAll = (cc.image + cc.video + cc.audio) + cur.length;
    if (effectiveInlineMedia.maxTotalCount != null && totalAll >= effectiveInlineMedia.maxTotalCount) {
      toast.error(t('Maximum {{count}} files allowed in total', { count: effectiveInlineMedia.maxTotalCount }));
      return;
    }
    const typeTotal = (cc[fileType as keyof typeof cc] ?? 0) + (curCounts[fileType as keyof typeof curCounts] ?? 0);
    if (typeTotal >= (typeCfg.maxCount ?? Infinity)) {
      toast.error(t('Maximum {{count}} {{type}} allowed', { count: typeCfg.maxCount, type: t(fileType) }));
      return;
    }
    const url = item.rawSrc ?? item.src;
    const attId = nanoid();
    const att: InlineAttachment = {
      id: attId,
      url,
      type: item.contentType || (fileType === 'video' ? 'video/mp4' : fileType === 'audio' ? 'audio/mpeg' : 'image/png'),
      fileType: fileType as InlineAttachment['fileType'],
      name: item.name,
    };
    const targetRole = assetPickerTargetRoleRef.current;
    assetPickerTargetRoleRef.current = undefined;
    if (targetRole) {
      att.role = targetRole;
    } else {
      const explicitMode = (latest?.frameMode as string | undefined) ?? 'none';
      if (fileType === 'image') {
        if (explicitMode === 'ref' || explicitMode === 'avatar') {
          att.role = 'reference';
        } else if (explicitMode === 'first_last') {
          if (!cur.some(a => a.role === 'start')) att.role = 'start';
          else if (!cur.some(a => a.role === 'end')) att.role = 'end';
        } else if (explicitMode === 'first') {
          if (!cur.some(a => a.role === 'start')) att.role = 'start';
        }
      }
    }
    updateNodeData(id, { inlineAttachments: [...cur, att].slice(0, 16) });

    if (fileType === 'video' || fileType === 'audio') {
      try {
        const dur = await getMediaDurationFromUrl(url, fileType);
        if (dur > 0) {
          const freshData = (getNode(id) as any)?.data;
          const freshAtts = Array.isArray(freshData?.inlineAttachments) ? freshData.inlineAttachments as InlineAttachment[] : [];
          updateNodeData(id, {
            inlineAttachments: freshAtts.map(a => a.id === attId ? { ...a, duration: dur } : a),
          });
        }
      } catch { /* duration unavailable, will be resolved at generation time */ }
    }
  }, [getNode, id, updateNodeData, t, effectiveInlineMedia, connectedCounts]);

  // 从主体库挑一个主体加为 inline image（受当前 frameMode + 上限约束）。
  // - mosaic 主体：1 张拼图作为 inline image
  // - multi 主体：在 ref/avatar 模式下加 N 张原图（按 maxCount 截断），其它模式只加首图
  const handlePickFromSubjectLibrary = useCallback((item: {
    id: string;
    name: string;
    image?: string | null;
    uploadedImage?: string[] | null;
    prompt?: string | null;
    mode?: 'mosaic' | 'multi';
  }) => {
    const latest = (getNode(id) as any)?.data;
    const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];
    const typeCfg = effectiveInlineMedia.image;
    if (!typeCfg?.allow) {
      toast.error(t('This model does not support {{type}} inline upload', { type: t('image') }));
      return;
    }

    const targetRole = subjectPickerTargetRoleRef.current;
    subjectPickerTargetRoleRef.current = undefined;
    const explicitMode = (latest?.frameMode as string | undefined) ?? 'none';
    const isRefLike = explicitMode === 'ref' || explicitMode === 'avatar';

    const mode = item.mode === 'multi' ? 'multi' : 'mosaic';
    const allImages: string[] = mode === 'multi' && Array.isArray(item.uploadedImage)
      ? (item.uploadedImage as string[]).map((u) => String(u || '').trim()).filter(Boolean)
      : [String(item.image || '').trim()].filter(Boolean);
    if (allImages.length === 0) return;

    // first / first_last / 指定 role 的槽位：只用首图（多张图无法对应单 slot）
    let candidate = isRefLike && !targetRole ? allImages : allImages.slice(0, 1);

    // 按上限截断
    const cc = connectedCounts;
    const totalAll = (cc.image + cc.video + cc.audio) + cur.length;
    const totalRoom = effectiveInlineMedia.maxTotalCount != null
      ? Math.max(0, effectiveInlineMedia.maxTotalCount - totalAll)
      : Infinity;
    const typeTotal = (cc.image ?? 0) + cur.filter(a => a.fileType === 'image').length;
    const typeRoom = typeCfg.maxCount != null
      ? Math.max(0, typeCfg.maxCount - typeTotal)
      : Infinity;
    const room = Math.min(totalRoom, typeRoom);
    if (room <= 0) {
      const limit = Math.min(
        effectiveInlineMedia.maxTotalCount ?? Infinity,
        typeCfg.maxCount ?? Infinity,
      );
      if (Number.isFinite(limit)) {
        toast.error(t('Maximum {{count}} {{type}} allowed', { count: limit, type: t('image') }));
      }
      return;
    }
    if (Number.isFinite(room) && candidate.length > (room as number)) {
      toast.error(t('Only added first {{count}} images due to limit', { count: room as number }));
      candidate = candidate.slice(0, room as number);
    }

    const subjectName = String(item.name || '').trim() || t('Subject');
    const added: InlineAttachment[] = candidate.map((url, i) => {
      const att: InlineAttachment = {
        id: nanoid(),
        url,
        type: 'image/png',
        fileType: 'image',
        name: candidate.length > 1 ? `${subjectName} ${i + 1}/${candidate.length}` : subjectName,
      };
      // role 决策：
      //   - 指定 targetRole（first/last/reference 槽触发）→ 该 role
      //   - ref/avatar 模式 → 'reference'
      //   - first_last 模式 → 第一张 start，第二张 end（仅在没有占用时填充）
      //   - first 模式 → 第一张 start
      if (targetRole) {
        att.role = targetRole;
      } else if (isRefLike) {
        att.role = 'reference';
      } else if (explicitMode === 'first_last') {
        if (i === 0 && !cur.some(a => a.role === 'start')) att.role = 'start';
        else if (i === 1 && !cur.some(a => a.role === 'end')) att.role = 'end';
      } else if (explicitMode === 'first') {
        if (i === 0 && !cur.some(a => a.role === 'start')) att.role = 'start';
      }
      return att;
    });
    updateNodeData(id, { inlineAttachments: [...cur, ...added].slice(0, 16) });
  }, [getNode, id, updateNodeData, t, effectiveInlineMedia, connectedCounts]);

  const openSubjectPicker = useCallback((role?: 'start' | 'end' | 'reference') => {
    subjectPickerTargetRoleRef.current = role;
    setSubjectPickerOpen(true);
  }, []);

  // ── Dedicated first/last frame slot helpers ───────────────────

  const handleFrameSlotUpload = useCallback(async (role: 'start' | 'end', files: FileList | null) => {
    if (!files?.length || !user?.id || !project?.id) return;
    setFrameSlotUploading(role);
    try {
      const token = await getToken({ template: 'supabase' });
      if (!token) throw new Error('No auth token');
      const file = files[0];
      if (!file.type.startsWith('image/')) return;
      const frameCfg = role === 'start' ? effectiveInlineMedia.firstFrame : effectiveInlineMedia.lastFrame;
      const imgCfg = effectiveInlineMedia.image;
      const formats = frameCfg?.formats ?? imgCfg?.formats;
      if (formats?.length) {
        const mime = file.type.toLowerCase();
        if (!formats.some(f => f === mime || (f.endsWith('/*') && mime.startsWith(f.slice(0, -1))))) {
          toast.error(t('Unsupported format {{format}}', { format: file.type || file.name.split('.').pop() }));
          return;
        }
      }
      const maxSize = frameCfg?.maxSize ?? imgCfg?.maxSize;
      if (maxSize && file.size > parseBytes(maxSize)) {
        toast.error(t('File exceeds size limit of {{size}}', { size: maxSize }));
        return;
      }
      const { url, type } = await uploadFile(file, 'files', {
        token, userId: user.id, projectId: String(project.id), assetType: 'image',
      });
      const latest = (getNode(id) as any)?.data;
      const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];
      const filtered = cur.filter((a) => !(a.fileType === 'image' && a.role === role));
      const newAtt: InlineAttachment = { id: nanoid(), url, type, fileType: 'image', name: file.name, role };
      updateNodeData(id, { inlineAttachments: [...filtered, newAtt] });
    } catch (error) {
      handleError(t('Upload failed'), error);
    } finally {
      setFrameSlotUploading(null);
      const ref = role === 'start' ? firstFrameInputRef : lastFrameInputRef;
      if (ref.current) ref.current.value = '';
    }
  }, [user?.id, project?.id, getToken, getNode, id, updateNodeData, t, effectiveInlineMedia]);

  const allImageInputsForMention = useMemo(() => {
    const isRefLike = currentFrameMode === 'ref' || currentFrameMode === 'avatar';

    if (isRefLike) {
      const result: typeof incomingImageInputs = [];
      const counts: Record<string, number> = { image: 0, video: 0, audio: 0 };
      // 用 incomingImageInputs 反查 multi 主体信息（sourceId -> subTotal）
      const subjectInfoBySource = new Map<string, { subTotal: number }>();
      for (const it of incomingImageInputs) {
        if (typeof it.subTotal === 'number' && it.subTotal > 1) {
          subjectInfoBySource.set(it.sourceId, { subTotal: it.subTotal });
        }
      }
      const subjectAggIndexBySource = new Map<string, number>();
      for (const item of displayRefImages) {
        if (!refActiveIds.has(item.id) || !item.url) continue;
        const mt = item.mediaType ?? 'image';

        // multi 主体「第一次见到的子图」前插入聚合项（不依赖首图是否被跳过）
        if (mt === 'image' && item.source === 'upstream' && item.sourceId) {
          const info = subjectInfoBySource.get(item.sourceId);
          if (info && !subjectAggIndexBySource.has(item.sourceId)) {
            const baseLabel = String(item.name || '').replace(/\s+\d+\/\d+\s*$/, '').trim();
            const aggIdx = result.length;
            subjectAggIndexBySource.set(item.sourceId, aggIdx);
            result.push({
              idx: 0,
              sourceId: item.sourceId,
              rid: `up:${item.sourceId}:group`,
              subTotal: info.subTotal,
              kind: 'subjectGroup',
              groupChildIdxs: [],
              url: item.url,
              label: baseLabel || t('Image Group'),
              mediaType: 'image',
            });
          }
        }

        counts[mt]++;
        const childResultIdx = result.length;
        result.push({
          idx: counts[mt],
          sourceId: item.source === 'upstream' ? item.sourceId! : `inline-${item.attId}`,
          rid: item.id,
          url: item.url,
          label: item.name || '',
          mediaType: mt,
        });

        if (mt === 'image' && item.source === 'upstream' && item.sourceId) {
          const aggIdx = subjectAggIndexBySource.get(item.sourceId);
          if (aggIdx !== undefined) {
            const agg = result[aggIdx];
            if (agg && Array.isArray(agg.groupChildIdxs)) agg.groupChildIdxs.push(childResultIdx);
          }
        }
      }
      return result;
    }

    const result: typeof incomingImageInputs = [];
    let imageIdx = 0;
    let videoIdx = 0;
    let audioIdx = 0;

    // multi 主体上游：在该主体的「第一张能用的子图」前面插入一个聚合项，
    // 用户在 mention 菜单选中聚合项时可一次性插入该主体所有子图的 mention。
    // 注意：首图（subIndex=0）可能因 URL 失效被跳过，所以以「sourceId 第一次出现」为准。
    const subjectAggIndexBySource = new Map<string, number>();
    for (const it of incomingImageInputs) {
      if (!it.url) continue;
      const isMultiChild = typeof it.subTotal === 'number' && it.subTotal > 1;
      if (isMultiChild && !subjectAggIndexBySource.has(it.sourceId)) {
        const baseLabel = String(it.label || '').replace(/\s+\d+\/\d+\s*$/, '').trim();
        const aggIdx = result.length;
        subjectAggIndexBySource.set(it.sourceId, aggIdx);
        result.push({
          idx: 0,
          sourceId: it.sourceId,
          rid: `up:${it.sourceId}:group`,
          subTotal: it.subTotal,
          kind: 'subjectGroup',
          groupChildIdxs: [],
          url: it.url,
          label: baseLabel || t('Image Group'),
          mediaType: 'image',
        });
      }

      imageIdx++;
      const childResultIdx = result.length;
      result.push({ ...it, idx: imageIdx, kind: 'image' });

      if (isMultiChild) {
        const aggIdx = subjectAggIndexBySource.get(it.sourceId);
        if (aggIdx !== undefined) {
          const agg = result[aggIdx];
          if (agg && Array.isArray(agg.groupChildIdxs)) agg.groupChildIdxs.push(childResultIdx);
        }
      }
    }

    for (const it of incomingVideoAudioInputs) {
      if (!it.url) continue;
      if (it.mediaType === 'video') videoIdx = it.idx;
      else audioIdx = it.idx;
      result.push({ idx: it.idx, sourceId: it.sourceId, rid: `up:${it.sourceId}`, url: it.url, label: it.label, mediaType: it.mediaType });
    }

    for (const att of inlineAttachments) {
      if (!att.url) continue;
      let idx: number;
      let label: string;
      let mediaType: 'image' | 'video' | 'audio';
      if (att.fileType === 'video') {
        videoIdx++;
        idx = videoIdx;
        label = att.name || t('Inline video');
        mediaType = 'video';
      } else if (att.fileType === 'audio') {
        audioIdx++;
        idx = audioIdx;
        label = att.name || t('Inline audio');
        mediaType = 'audio';
      } else {
        imageIdx++;
        idx = imageIdx;
        label = att.name || t('Inline image');
        mediaType = 'image';
      }
      result.push({ idx, sourceId: `inline-${att.id}`, rid: `in:${att.id}`, url: att.url, label, mediaType });
    }
    return result;
  }, [currentFrameMode, displayRefImages, refActiveIds, incomingImageInputs, incomingVideoAudioInputs, inlineAttachments, t]);


  // 可用的视频 URL：优先使用本节点生成的视频（且类型为 video/*），其次使用上游视频，最后才尝试 sourceUrl 且需为视频链接
  const availableVideoUrl = useMemo(() => {
    const generatedUrl = (data as any)?.generated?.type?.startsWith?.("video")
      ? (data as any)?.generated?.url
      : undefined;
    // Asset:// 引用不可直接播放，用提交审核时保存的预览 URL 代替
    const playableUrl = resolveAssetRefPreview(generatedUrl, assetPreviewUrl);
    if (playableUrl) return playableUrl;
    if (generatedUrl) return undefined; // Asset:// 引用但预览缺失：不回退到上游视频
    const upVids = getVideosFromVideoNodes(incomersForOptions) as any[];
    if (upVids.length) {
      const upUrl = typeof upVids[0] === "string" ? upVids[0] : upVids[0]?.url;
      // 上游视频已提交审核（Asset:// 引用）时无法直接播放/抽帧，解析回其预览 URL
      if (isAssetRefUrl(upUrl)) {
        return resolveNodeAssetRefUrls([{ url: upUrl }], incomersForOptions as any)[0]?.url;
      }
      return upUrl;
    }
    const src = (data as any)?.sourceUrl as string | undefined;
    return src && isVideoLikeUrl(String(src)) ? String(src) : undefined;
  }, [incomersForOptions, (data as any)?.generated?.url, (data as any)?.generated?.type, (data as any)?.sourceUrl, assetPreviewUrl]);

  const extractFrame = useCallback(async (mode: "first" | "last") => {
    if (!availableVideoUrl) {
      toast.error(t("No video available to extract"));
      return;
    }
    if (!isVideoLikeUrl(String(availableVideoUrl))) {
      toast.error(t("Selected source is not a video URL"));
      return;
    }
    await extractToImageNode(id, mode, String(availableVideoUrl));
  }, [availableVideoUrl, id, extractToImageNode, t]);

  // 订阅视频生成的 Realtime 状态（生成专用 Realtime）
  useVideoRealtime({
    projectId: project?.id,
    nodeId: id,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
  });

  // 轮询
  useVideoPolling({
    projectId: project?.id,
    nodeId: id,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
  });

  const generate = useCallback(async (opts?: { interactive?: boolean; overrideData?: Record<string, any> }) => {
    try {
      const ok = await createVideoHandleGenerate({
        t,
        project,
        nodeId: id,
        getNodes,
        getEdges,
        data: opts?.overrideData ? { ...data, ...opts.overrideData } : data,
        modelId,
        updateNodeData,
        // 交互式生成：precheck 缺失输入时弹窗提醒，而不是 toast；自动生成（运行全部）保持原 toast 行为。
        onPrecheckFail: opts?.interactive
          ? (missing) => setPrecheckMissing(missing)
          : undefined,
        // precheck 通过后才显示 loading，避免缺输入弹窗时按钮闪一下转圈。
        onBeforeGenerate: () => setGenerating(true),
      })();
      if (ok) consumeModelPass(modelId);
    } catch {
      // inner handler already handles errors; this is a safety net
    } finally {
      setGenerating(false);
    }
  }, [t, project, id, getNodes, getEdges, data, modelId, updateNodeData, consumeModelPass]);

  // 当前模型是否支持「图生视频（首帧）」模式，用于 t2v 误连图时引导切换。
  const supportsFirstFrame = useMemo(
    () => frameModeOptions.some((o) => o.value === 'first'),
    [frameModeOptions]
  );

  const handleGenerate = useCallback(() => {
    try {
      // 上游连了图片但当前是文生视频（frameMode='none'），图片会被忽略：提醒切换到图生视频。
      if (currentFrameMode === 'none' && supportsFirstFrame) {
        const upstreamImages = getAllImagesFromNodes(incomers as any, getEdges(), id) || [];
        if (upstreamImages.length > 0) {
          setT2vImageDialogOpen(true);
          return;
        }
      }
    } catch {
      // 检测失败时不阻断正常生成
    }
    void generate({ interactive: true });
  }, [currentFrameMode, supportsFirstFrame, incomers, getEdges, id, generate]);

  const handleCancel = useCallback(() => {
    const gid = (getNode(id) as any)?.data?.processingGenerationId as string | undefined;
    updateNodeData(id, {
      status: undefined,
      processingGenerationId: undefined,
      runtimeEstimatedTime: undefined,
      error: undefined,
      updatedAt: new Date().toISOString(),
    });
    toast(t('Generation cancelled'));
    if (gid) void cancelGeneration(gid);
  }, [id, getNode, updateNodeData, t]);

  // Auto-generate event hook (central router)
  useAutoGenerateHandler(
    id,
    useCallback(() => {
      try {
        // 画布批量「运行全部」不弹交互式弹窗，precheck 缺失沿用原 toast/failed 行为。
        void generate({ interactive: false });
      } catch {}
    }, [generate]),
  );

  // Auto-cancel hook: lets the canvas-wide "Stop" button abort this node's
  // in-flight generation instead of letting it run to completion.
  useAutoCancelHandler(
    id,
    useCallback(() => {
      try {
        if ((data as any)?.status === "processing") handleCancel();
      } catch {}
    }, [data, handleCancel]),
  );

  // 输入时只更新本地状态，不立即保存
  const handleInstructionsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalInstructions(e.target.value);
  }, []);

  // 失去焦点时保存到节点数据
  const handleInstructionsBlur = useCallback(() => {
    setInstructionsFocused(false);
    // 只在值发生变化时才保存
    if (localInstructions !== ((data as any)?.instructions ?? '')) {
      updateNodeData(id, { instructions: localInstructions });
    }
  }, [id, updateNodeData, localInstructions, data]);

  // 卸载时刷写未提交的输入到 store（关闭“渲染全部节点”后节点移出视口会被卸载且不触发 blur）。
  useFlushInstructionsOnUnmount(localInstructions, (data as any)?.instructions ?? '', commitInstructionsToStore);

  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(36, textareaRef.current.scrollHeight) + 'px';
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localInstructions]);

  /**
   * 删除内联素材或断开上游连线后，重写 instructions 中的 @[图像N]/@[视频N]/@[音频N] 引用：
   * 被删项对应的引用被移除，更大的同类型序号依次前移补位。
   */
  const rewriteInstructionsForRemoval = useCallback(
    (shouldRemove: (it: { sourceId: string; idx: number; mediaType?: string; kind?: string }) => boolean) => {
      const baseline = deferredRewrite.getBaselineText();
      if (!baseline) return;
      const next = rewriteMentionsAfterRemoval(baseline, allImageInputsForMention, shouldRemove as any, mentionLabels);
      if (next !== baseline) deferredRewrite.commitNow(next);
    },
    [allImageInputsForMention, mentionLabels, deferredRewrite],
  );

  const videoToolbar: ComponentProps<typeof NodeLayout>["toolbar"] =
    useMemo(() => {
      let configControls: any = null;
      const actionControls: any[] = [];
      const pushAction = (el: any) => actionControls.push(el);
      const COL_W: number | string | 'auto' = '100%';

      const accessFiltered = filterModelsClient(videoModels, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels);
      // 存在上游视频时，不支持视频转视频的模型不再隐藏，而是保留并打上标记，
      // 由 ModelSelector 显示提示（保持“可见但提醒”体验，与 image 节点一致）。
      const filteredVideoModels = hasIncomingVideoNodes
        ? markIncompatibleModels(accessFiltered, 'v2v')
        : accessFiltered;

      const modelControl = (
        <ModelItem
          id={id}
          value={modelId}
          options={filteredVideoModels}
          width={COL_W}
          onChange={(value) => {
              rememberLastSelectedModelId('video', value);
              // 与 image 节点对齐：切换模型视为“重置配置”
              // - 清除旧的 generated（避免旧模型产物与新模型配置混用）
              // - 清除旧的 modelUi（不同模型的 uiOptions key 往往不同）
              // - 取消选中的历史记录（历史数据仍保留）
              const nextData: any = {
                model: value,
                generated: undefined,
                modelUi: {},
                activeHistoryId: undefined,
              };
              const vm = videoModels[value];
              const vmCfg = getEffectiveConfig(vm, currentFrameMode);
              const allowedARs = vmCfg.aspectRatios;
              const allowedDurations = vmCfg.durations;
              if (Array.isArray(allowedARs)) {
                const presetAR = (() => {
                  try {
                    const raw = getPresetAspectRatio();
                    return raw ? String(raw).trim() : undefined;
                  } catch {
                    return undefined;
                  }
                })();

                // aspectRatios: [] => Auto（清理旧残留值）
                if (allowedARs.length === 0) {
                  nextData.aspectRatio = undefined;
                } else if (presetAR && allowedARs.includes(presetAR)) {
                  // 切换模型视为“重置配置”：若存在预设比例且新模型支持，则优先跟随预设比例
                  nextData.aspectRatio = presetAR;
                } else {
                  // 无预设 / 新模型不支持预设：尽量保留旧值（若合法），否则回落到模型默认第一个
                  const rawAR = (data as any)?.aspectRatio;
                  const cur =
                    typeof rawAR === "string" ? rawAR : String(rawAR ?? "");
                  if (cur && allowedARs.includes(cur)) {
                    nextData.aspectRatio = cur;
                  } else {
                    nextData.aspectRatio = allowedARs[0];
                  }
                }
              }
              // durations: [] => Auto（清理旧残留值）；否则写入默认值（第一个可用项）
              if (Array.isArray(allowedDurations)) {
                if (allowedDurations.length === 0) {
                  nextData.duration = undefined;
                } else {
                  const raw = (data as any)?.duration;
                  const isUnset =
                    typeof raw === "undefined" || raw === null || raw === "" || raw === "Auto";
                  if (isUnset) {
                    nextData.duration = allowedDurations[0];
                  } else {
                    const cur = Number(raw);
                    if (!Number.isFinite(cur) || cur <= 0 || !allowedDurations.includes(cur)) {
                      nextData.duration = allowedDurations[0];
                    }
                  }
                }
              }
              const allowedQualities = (() => {
                const map = vmCfg.qualityByDuration;
                const durForQualities = Number(nextData.duration ?? (data as any)?.duration);
                if (map && Number.isFinite(durForQualities) && map[durForQualities] && Array.isArray(map[durForQualities]) && map[durForQualities].length) {
                  return map[durForQualities];
                }
                return vmCfg.qualities;
              })() as string[] | undefined;
              // qualities: [] => Auto（清理旧残留值）；否则写入默认值（第一个可用项）
              if (Array.isArray(allowedQualities)) {
                if (allowedQualities.length === 0) {
                  nextData.quality = undefined;
                  // 兼容旧字段
                  nextData.resolution = undefined;
                } else {
                  const rawQ = (data as any)?.quality ?? (data as any)?.resolution;
                  const isUnset =
                    typeof rawQ === "undefined" || rawQ === null || rawQ === "" || rawQ === "Auto";
                  if (isUnset) {
                    nextData.quality = allowedQualities[0];
                    nextData.resolution = undefined;
                  } else if (!allowedQualities.includes(String(rawQ))) {
                    nextData.quality = allowedQualities[0];
                    nextData.resolution = undefined;
                  }
                }
              }
              updateNodeData(id, nextData);
          }}
          disabled={isToolbarBlocked}
        />
      );

      // 尺寸/时长/质量：构建参数控件（存在时才渲染分割线与控件）
      const arOptions = (ec.aspectRatios ?? []).map((ar) => ({ value: ar, label: ar }));
      const durOptions = (ec.durations ?? []).map((d) => ({ value: String(d), label: `${d}s` }));
      const qOptions = (() => {
        const map = ec.qualityByDuration;
        const curDur = Number((data as any)?.duration);
        const list = map && Number.isFinite(curDur) && map[curDur] && Array.isArray(map[curDur]) && map[curDur].length ? map[curDur] : ec.qualities;
        return (list ?? []).map((q) => ({ value: q, label: q }));
      })();

      const arControl = (
        <VideoAspectRatioSelector
          id={`${id}-ar`}
          value={(() => {
            if (!arOptions.length) return "Auto";
            const raw = (data as any)?.aspectRatio;
            const cur = typeof raw === "string" ? raw : String(raw ?? "");
            if (cur && arOptions.some((o) => o.value === cur)) return cur;
            return arOptions[0]?.value ?? "";
          })()}
          options={arOptions.length ? arOptions.map((o) => o.value) : ["Auto"]}
          onChange={(val) => {
            if (val === "Auto" || val === "") {
              updateNodeData(id, {
                aspectRatio: undefined,
                generated: undefined,
                activeHistoryId: undefined,
              });
              return;
            }
            updateNodeData(id, {
              aspectRatio: val,
              generated: undefined,
              activeHistoryId: undefined,
            });
          }}
          disabled={isToolbarBlocked || arOptions.length === 0}
          width={COL_W}
        />
      );
      const durControl = (
        <VideoDurationSelector
          id={`${id}-dur`}
          value={(() => {
            const raw = (data as any)?.duration;
            const cur = Number(raw);
            if (
              Number.isFinite(cur) &&
              cur > 0 &&
              durOptions.some((o) => o.value === String(cur))
            ) {
              return String(cur);
            }
            // 仅当模型未提供 durations 时才展示 Auto；否则回落到第一个可选项
            return durOptions.length ? (durOptions[0]?.value ?? "Auto") : "Auto";
          })()}
          options={durOptions.length ? durOptions.map((o) => o.value) : ["Auto"]}
          onChange={(val) => {
            if (val === "Auto" || val === "") {
              updateNodeData(id, {
                duration: undefined,
                durationSeconds: undefined,
                generated: undefined,
                activeHistoryId: undefined,
              });
              return;
            }
            const newDur = Number(val);
            const filteredQs = (() => {
              const map = ec.qualityByDuration;
              return map && map[newDur] && Array.isArray(map[newDur]) && map[newDur].length ? map[newDur] : ec.qualities;
            })();
            const next: any = {
              duration: newDur,
              durationSeconds: undefined,
              generated: undefined,
              activeHistoryId: undefined,
            };
            if (Array.isArray(filteredQs) && filteredQs.length) {
              const curQ = (data as any)?.quality as string | undefined;
              if (!curQ || !filteredQs.includes(curQ)) {
                next.quality = filteredQs[0];
              }
            }
            updateNodeData(id, next);
          }}
          disabled={isToolbarBlocked || durOptions.length === 0}
          width={COL_W}
        />
      );
      const qualityControl = (
        <VideoQualitySelector
          id={`${id}-quality`}
          value={(() => {
            const rawQ = (data as any)?.quality ?? (data as any)?.resolution;
            if (typeof rawQ === "string" && rawQ && qOptions.some((o) => o.value === rawQ)) {
              return rawQ;
            }
            // 仅当模型未提供 qualities 时才展示 Auto；否则回落到第一个可选项
            return qOptions.length ? (qOptions[0]?.value ?? "Auto") : "Auto";
          })()}
          options={qOptions.length ? qOptions.map((o) => o.value) : ["Auto"]}
          onChange={(val) => {
            if (val === "Auto" || val === "") {
              updateNodeData(id, {
                quality: undefined,
                resolution: undefined,
                generated: undefined,
                activeHistoryId: undefined,
              });
              return;
            }
            updateNodeData(id, {
              quality: val,
              resolution: undefined,
              generated: undefined,
              activeHistoryId: undefined,
            });
          }}
          disabled={isToolbarBlocked || qOptions.length === 0}
          width={COL_W}
        />
      );

      // Inline UI options (rendered between config and action rows)
      const inlineUiOptions = uiOptions.filter((o) => o.group === 'inline');
      const panelUiOptions = uiOptions.filter((o) => o.group !== 'inline' && o.group !== 'hidden');

      const onInlineOptionChange = (key: string, val: unknown) => {
        if (key === 'multi_shots' && val === false) {
          setLocalInstructions('');
          updateNodeData(id, { modelUi: { ...(modelUi || {}), [key]: val }, instructions: '' });
          return;
        }
        updateNodeData(id, { modelUi: { ...(modelUi || {}), [key]: val } });
      };

      // 内联选项（select / switch）统一走独立 options 行，与图片节点一致
      const middleRowElements = inlineUiOptions
        .map((opt) => {
          if (opt.type === 'switch') {
            const val = typeof modelUi?.[opt.key] === 'boolean' ? modelUi[opt.key] as boolean : opt.default;
            return (
              <label
                key={opt.key}
                htmlFor={`${id}-${opt.key}`}
                className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-2"
              >
                <span className="inline-flex min-w-0 items-center gap-1 leading-none">
                  <span className="truncate">{t(opt.label)}</span>
                  {opt.remark ? <OptionRemark text={t(opt.remark)} /> : null}
                </span>
                <Switch
                  id={`${id}-${opt.key}`}
                  checked={val}
                  onCheckedChange={(v) => onInlineOptionChange(opt.key, v)}
                  disabled={isToolbarBlocked || opt.readonly}
                />
              </label>
            );
          }
          if (opt.type === 'select') {
            const val = typeof modelUi?.[opt.key] === 'string' ? modelUi[opt.key] as string : (opt.default as string);
            return (
              <div key={opt.key} className="min-w-0">
                <InlineComboboxSelect
                  id={`${id}-${opt.key}`}
                  label={t(opt.label)}
                  value={val}
                  // 选项文案作为 i18n key 翻译；无对应词条时 t() 原样回退
                  options={(opt.options ?? []).map((o) => ({ ...o, label: t(o.label) }))}
                  onChange={(v) => onInlineOptionChange(opt.key, v)}
                  disabled={isToolbarBlocked || opt.readonly}
                  showIcon={false}
                />
              </div>
            );
          }
          return null;
        }).filter(Boolean);

      // 与「下载 / 历史 / 高级」共用 TOOLBAR_GHOST_ITEM_CN，左缘与字号对齐
      const middleControls = middleRowElements.length ? (
        <InlineOptionsRow>{middleRowElements}</InlineOptionsRow>
      ) : undefined;

      // Row 2 layout: 模型 / 比例 / 秒 / 分辨率(质量)
      // Each selector can be hidden per-model; the model selector always shows and absorbs the freed space.
      const showAr = !Boolean((selectedModel as any)?.hideAspectRatio);
      const showDur = !Boolean((selectedModel as any)?.hideDuration);
      const showQuality = !Boolean((selectedModel as any)?.hideQuality);
      const configCols = [
        '17fr',
        showAr ? '7fr' : null,
        showDur ? '7fr' : null,
        showQuality ? '8fr' : null,
      ]
        .filter(Boolean)
        .join(' ');
      configControls = (
        <div className="grid gap-1.5 w-full items-center" style={{ gridTemplateColumns: configCols }}>
          <div className="min-w-0">{modelControl}</div>
          {showAr && <div className="min-w-0">{arControl}</div>}
          {showDur && <div className="min-w-0">{durControl}</div>}
          {showQuality && <div className="min-w-0">{qualityControl}</div>}
        </div>
      );

      // Split to action row
      // (actions start here)

      // 生成按钮：放到动作行最右侧
      const generateControl =
        (data as any)?.status === "processing" ? (
          <GenerateItem
            key="generate"
            loading
            generatedUrl={undefined}
            onGenerate={() => {}}
            onCancel={handleCancel}
            disabled
            estimatedCredits={estimatedCredits}
            passRemaining={passRemaining}
            passCount={passCount}
            discountMultiplier={discountMultiplier}
            passDiscountMultiplier={passDiscountMultiplier}
          />
        ) : (
          <GenerateItem
            key="generate"
            loading={generating}
            generatedUrl={(data as any)?.generated?.url}
            onGenerate={() => { void handleGenerate(); }}
            disabled={!project?.id || generating}
            estimatedCredits={estimatedCredits}
            passRemaining={passRemaining}
            passCount={passCount}
            discountMultiplier={discountMultiplier}
            passDiscountMultiplier={passDiscountMultiplier}
          />
        );

      // Download button (always render; disable when not available)
      const generated = (data as any)?.generated as { url?: string; type?: string } | undefined;
      // Asset:// 引用不可直接下载，用提交审核时保存的预览 URL 代替
      const generatedUrl = resolveAssetRefPreview(generated?.url, assetPreviewUrl);
      const hasGenerated = Boolean(generatedUrl);
      pushAction(
        <DownloadItem
          key="download"
          onDownload={() => {
            const url = generatedUrl;
            if (!url) return;
            download({ url, type: generated?.type ?? "video/mp4" }, id, "mp4");
          }}
          onDownloadOriginal={
            (data as any)?.originalUrl
              ? () =>
                  download(
                    { url: (data as any).originalUrl, type: "video/mp4" },
                    id,
                    "mp4"
                  )
              : undefined
          }
          disabled={!hasGenerated || isToolbarBlocked}
          downloadData={hasGenerated ? { url: generatedUrl, type: generated?.type ?? "video/mp4" } : undefined}
        />
      );

      // History (always render; disable when not available)
      const historyData = (data as any).history as any[] | undefined;
      const activeHistoryId = (data as any).activeHistoryId as
        | string
        | undefined;
      pushAction(
        <HistoryItem
          key="history"
          items={historyData}
          activeId={activeHistoryId}
          showPreview={false}
          disabled={isToolbarBlocked || !Array.isArray(historyData) || historyData.length === 0}
          onSelect={(rec) => {
                // 复用原逻辑：根据历史记录回填参数
                const newModelId = (rec as any).modelId as string | undefined;
                const newModelMeta = newModelId
                  ? videoModels[newModelId]
                  : undefined;
                const nextInstructions = (rec as any)?.prompt ?? '';
                // 同时更新本地状态和节点数据
                setLocalInstructions(nextInstructions);
                const next: any = {
                  generated: { url: (rec as any).url, type: "video/mp4" },
                  activeHistoryId: (rec as any).id,
                  instructions: nextInstructions,
                  status: 'completed',
                  error: undefined,
                };
                if (newModelId) {
                  next.model = newModelId;
                  const histFrameMode = (rec as any).modelUi?.frameMode ?? currentFrameMode;
                  const histCfg = getEffectiveConfig(newModelMeta, histFrameMode);
                  const allowedARs = histCfg.aspectRatios;
                  const allowedDurations = histCfg.durations;
                  const histAR = (rec as any).aspectRatio as string | undefined;
                  const histDur = Number((rec as any).duration);
                  const histQ = (rec as any).quality as string | undefined;
                  const qbdMap = histCfg.qualityByDuration;
                  const allowedQualities = (qbdMap && Number.isFinite(histDur) && Array.isArray(qbdMap[histDur]) && qbdMap[histDur].length)
                    ? qbdMap[histDur]
                    : histCfg.qualities;
                  if (Array.isArray(allowedARs) && allowedARs.length)
                    next.aspectRatio = allowedARs.includes(String(histAR))
                      ? histAR
                      : allowedARs[0];
                  if (
                    Array.isArray(allowedDurations) &&
                    allowedDurations.length
                  )
                    next.duration =
                      Number.isFinite(histDur) &&
                      allowedDurations.includes(histDur)
                        ? histDur
                        : allowedDurations[0];
                  if (
                    Array.isArray(allowedQualities) &&
                    allowedQualities.length
                  )
                    next.quality =
                      typeof histQ === "string" &&
                      allowedQualities.includes(histQ)
                        ? histQ
                        : allowedQualities[0];
                }
                if ((rec as any).modelUi) {
                  next.modelUi = (rec as any).modelUi;
                  if ((rec as any).modelUi.frameMode) next.frameMode = (rec as any).modelUi.frameMode;
                }
                // 真实尺寸/时长跟随该历史条目：条目带有真实值时直接采用（切换即权威，无需等 <video> 重新加载测量）；
                // 缺失时清空，避免沿用上一条目的过期分辨率（surfaceAspectRatio 会回退到已恢复的比例设置）。
                // 真实分辨率统一存于 generated.size（"宽x高"）+ sizeUrl，与 image 一致。
                {
                  const recDims = parseVideoDimensions(rec);
                  const recUrl = (rec as any).url as string | undefined;
                  if (recDims && recUrl) {
                    next.generated = { ...(next.generated as object), size: `${recDims.width}x${recDims.height}`, sizeUrl: recUrl };
                  }
                  const recDur = Number((rec as any).durationSeconds);
                  next.durationSeconds = Number.isFinite(recDur) && recDur > 0 ? recDur : undefined;
                  // 清理旧数据可能残留的顶层 width/height，统一由 generated.size 承载。
                  next.width = undefined;
                  next.height = undefined;
                }
                updateNodeData(id, next);
          }}
        />
      );

      // Advanced options: inline expandable panel (grows the toolbar height)
      const advancedDisabled = isToolbarBlocked || panelUiOptions.length === 0;
      const advancedExpanded = advancedOpen && !advancedDisabled;
      pushAction(
        <AdvancedToggleButton
          key="advanced"
          expanded={advancedExpanded}
          disabled={advancedDisabled}
          onToggle={() => setAdvancedOpen((v) => !v)}
        />
      );

      // Put generate button at the far right of the action row
      pushAction(
        <div key="generate-right" className="ml-auto flex items-center">
          {generateControl}
        </div>
      );

      return [
        {
          children: (
            <ThreeRowToolbar
              showInstructions
              fixedWidthPx={720}
              instructions={(
                <div className="flex w-full flex-col gap-2 px-2 py-2">
                  {/* Frame mode selector */}
                  {frameModeOptions.length >= 1 && (
                    <div className="flex items-center gap-1.5 px-0.5">
                      {frameModeOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={isToolbarBlocked}
                          onClick={() => handleFrameModeChange(opt.value)}
                          className={cn(
                            "nodrag h-6 px-2.5 rounded-full text-[11px] font-medium transition-colors border",
                            currentFrameMode === opt.value
                              ? "bg-primary/10 text-primary border-primary/30"
                              : "text-muted-foreground/40 border-transparent hover:text-muted-foreground/70 hover:bg-muted/30",
                            "disabled:opacity-30 disabled:pointer-events-none"
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                      <span className="flex-1 text-center text-[11px] text-muted-foreground/30 select-none">{t('Drag to reorder')}</span>
                      <button
                        type="button"
                        className="nodrag ml-auto shrink-0 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-primary/70 hover:text-primary transition-colors"
                        onClick={() => instructionInputRef.current?.openEditor()}
                      >
                        <Maximize2 size={12} />
                        {t('Expand Prompt Editor')}
                      </button>
                    </div>
                  )}
                  {/* ── Frame-specific image area (first / first_last) ── */}
                  {(currentFrameMode === 'first' || currentFrameMode === 'first_last') && (() => {
                    const frameMaxActive = effectiveInlineMedia.image?.maxCount ?? Infinity;
                    const activeCount = Math.min(frameRoleSlots.length, frameMaxActive);
                    const posLabels = currentFrameMode === 'first' ? [t('First')] : [t('First'), t('Last')];
                    const missingSlots: { role: 'start' | 'end'; ref: React.RefObject<HTMLInputElement | null>; label: string }[] = [];
                    if (frameImages.length < 1) missingSlots.push({ role: 'start', ref: firstFrameInputRef, label: t('First') });
                    if (currentFrameMode === 'first_last' && frameImages.length < 2) missingSlots.push({ role: 'end', ref: lastFrameInputRef, label: t('Last') });

                    return (
                      <div className="px-0.5">
                        <DndContext sensors={inlineSortSensors} collisionDetection={closestCenter} onDragEnd={handleFrameImageReorder}>
                          <SortableContext items={frameImages.map((i) => i.key)} strategy={rectSortingStrategy}>
                            <div className="grid grid-cols-7 gap-1.5">
                              {frameImages.map((img, idx) => {
                                const active = idx < activeCount;
                                const label = idx < posLabels.length ? posLabels[idx] : t('Candidate');
                                return (
                                  <div key={img.key} className="relative">
                                    <SortableGridItem id={img.key} disabled={isToolbarBlocked}>
                                      <div className={`relative w-full aspect-square bg-muted/30 overflow-hidden group rounded-lg transition-all ${!active ? 'opacity-35 grayscale' : ''}`}>
                                        {img.url.startsWith('Asset://') ? (
                                          <div className="flex h-full w-full items-center justify-center bg-primary/5">
                                            <Package size={14} className="text-primary/60" />
                                          </div>
                                        ) : (
                                          <VariantImage url={img.url} transform={REF_THUMB_TRANSFORM} width={96} height={96} className="h-full w-full object-cover" />
                                        )}
                                        {img.src === 'inline' && !isToolbarBlocked && (
                                          <button type="button" className="absolute top-0 left-0 h-4 w-4 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={(e) => {
                                            e.preventDefault(); e.stopPropagation();
                                            rewriteInstructionsForRemoval((it) => it.sourceId === `inline-${img.id}`);
                                            handleInlineAttachmentsChange(inlineAttachments.filter((a) => a.id !== img.id));
                                          }}>
                                            <XIcon size={8} />
                                          </button>
                                        )}
                                        {img.src === 'upstream' && (
                                          <button type="button" className="absolute inset-0 cursor-pointer opacity-0" onClick={() => focusNodeById(img.sourceId!)} />
                                        )}
                                        {img.src === 'upstream' && img.sourceId && !isToolbarBlocked && (
                                          <button
                                            type="button"
                                            className="absolute top-0 left-0 h-4 w-4 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-20"
                                            title={t("Disconnect")}
                                            onClick={(e) => {
                                              e.preventDefault(); e.stopPropagation();
                                              const sid = img.sourceId!;
                                              rewriteInstructionsForRemoval((it) => it.sourceId === sid);
                                              setEdges((eds) => eds.filter((edge) => !(edge.source === sid && edge.target === id)));
                                            }}
                                          >
                                            <XIcon size={8} />
                                          </button>
                                        )}
                                      </div>
                                    </SortableGridItem>
                                    <div className={`pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 ${active ? 'bg-black/50' : 'bg-black/30'}`}>{label}</div>
                                  </div>
                                );
                              })}
                              {missingSlots.map((slot) => (
                                <div key={slot.role} className="relative">
                                  {frameSlotUploading === slot.role ? (
                                    <div className="relative w-full aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex items-center justify-center">
                                      <Loader2Icon size={12} className="animate-spin text-muted-foreground" />
                                    </div>
                                  ) : (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild disabled={isToolbarBlocked}>
                                        <button type="button" disabled={isToolbarBlocked} className="nodrag relative w-full aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:bg-muted/20 transition-colors disabled:opacity-30 disabled:pointer-events-none">
                                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                                        <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => slot.ref.current?.click()}>
                                          <UploadIcon size={14} className="mr-1.5" />
                                          {t("Upload media inline")}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openAssetPicker('image', slot.role)}>
                                          <LibraryBigIcon size={14} className="mr-1.5" />
                                          {t("Pick from library")}
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 bg-black/30">{slot.label}</div>
                                </div>
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    );
                  })()}
                  {(currentFrameMode === 'ref' || currentFrameMode === 'avatar') && (() => {
                    return (
                    <DraggableMediaGrid
                      items={displayRefImages}
                      sensors={inlineSortSensors}
                      onReorder={handleReferenceReorder}
                      isActive={(item) => refActiveIds.has(item.id)}
                      getLabel={(item) => refItemLabels.get(item.id) ?? t('Candidate')}
                      onRemoveInline={(attId) => {
                        rewriteInstructionsForRemoval((it) => it.sourceId === `inline-${attId}`);
                        handleInlineAttachmentsChange(inlineAttachments.filter((a) => a.id !== attId));
                      }}
                      onDisconnectUpstream={(sourceId) => {
                        rewriteInstructionsForRemoval((it) => it.sourceId === sourceId);
                        setEdges((eds) => eds.filter((e) => !(e.source === sourceId && e.target === id)));
                      }}
                      disabled={isToolbarBlocked}
                      isUploading={inlineUploading}
                      renderContent={(item) => {
                        if (item.url && item.url.startsWith('Asset://') && !item.previewUrl) {
                          return (
                            <div className="flex h-full w-full items-center justify-center bg-primary/5">
                              <Package size={14} className="text-primary/60" />
                            </div>
                          );
                        }
                        const displayUrl = item.previewUrl ?? item.url;
                        const isImg = item.mediaType === 'image';
                        if (isImg && displayUrl) return <VariantImage url={displayUrl} transform={REF_THUMB_TRANSFORM} width={96} height={96} className="h-full w-full object-cover" />;
                        if (item.url && item.mediaType === 'video') return (
                          <video
                            ref={(el) => { if (el) refMediaEls.current.set(item.id, el); else refMediaEls.current.delete(item.id); }}
                            src={item.url}
                            muted loop playsInline preload="metadata"
                            className="h-full w-full object-cover"
                            onEnded={() => { if (playingRefId === item.id) setPlayingRefId(null); }}
                          />
                        );
                        if (item.url && item.mediaType === 'audio') return (
                          <div className="flex h-full w-full items-center justify-center bg-muted/40">
                            <audio
                              ref={(el) => { if (el) refMediaEls.current.set(item.id, el); else refMediaEls.current.delete(item.id); }}
                              src={item.url} preload="metadata"
                              onEnded={() => { if (playingRefId === item.id) setPlayingRefId(null); }}
                            />
                            {playingRefId === item.id ? <PauseIcon size={14} className="text-emerald-400" /> : <MusicIcon size={14} className="text-emerald-400" />}
                          </div>
                        );
                        return (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-0.5">
                            {isImg ? <ImageIcon size={12} className="text-muted-foreground/40" /> : item.mediaType === 'video' ? <VideoIcon size={12} className="text-sky-400/40" /> : <MusicIcon size={12} className="text-emerald-400/40" />}
                            <span className="text-[7px] leading-tight text-muted-foreground/40">{t('Pending')}</span>
                          </div>
                        );
                      }}
                      renderOverlay={(item) => {
                        if (item.url && item.mediaType !== 'image') return (
                          <button
                            type="button"
                            className="absolute inset-0 flex items-center justify-center cursor-pointer z-[5]"
                            onClick={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              const el = refMediaEls.current.get(item.id);
                              if (!el) return;
                              if (playingRefId === item.id) { el.pause(); setPlayingRefId(null); }
                              else {
                                const prev = playingRefId ? refMediaEls.current.get(playingRefId) : null;
                                if (prev) { prev.pause(); prev.currentTime = 0; }
                                el.play().catch(() => {});
                                setPlayingRefId(item.id);
                              }
                            }}
                          >
                            {item.mediaType === 'video' && playingRefId !== item.id && (
                              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-black/50"><PlayIcon size={10} className="text-white" /></div>
                            )}
                          </button>
                        );
                        if (item.source === 'upstream' && !item.url) return <button type="button" className="absolute inset-0 cursor-pointer opacity-0" onClick={() => focusNodeById(item.sourceId!)} />;
                        return null;
                      }}
                      extraSlots={!isToolbarBlocked && !inlineUploading ? (() => {
                        const slots: { type: 'image' | 'video' | 'audio'; icon: React.ReactNode; label: string }[] = [];
                        const filled = { image: 0, video: 0, audio: 0 };
                        for (const ri of displayRefImages) filled[ri.mediaType]++;
                        let hasNonSlotRoom = false;
                        for (const key of ['image', 'video', 'audio'] as const) {
                          const tc = effectiveInlineMedia[key];
                          if (!tc?.allow) continue;
                          if (filled[key] >= (tc.maxCount ?? Infinity)) continue;
                          if (!tc.slot) { hasNonSlotRoom = true; continue; }
                          const icon = key === 'image' ? <ImageIcon size={12} className="text-muted-foreground/50" />
                            : key === 'video' ? <VideoIcon size={12} className="text-sky-400/50" />
                            : <MusicIcon size={12} className="text-emerald-400/50" />;
                          const label = typeof tc.slot === 'string' ? t(tc.slot) : t(key === 'image' ? 'Image' : key === 'video' ? 'Video' : 'Audio');
                          slots.push({ type: key, icon, label });
                        }
                        return (
                          <>
                            {slots.map((slot) => (
                              <div key={slot.type} className="relative">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild disabled={isToolbarBlocked}>
                                    <button type="button" disabled={isToolbarBlocked} className="nodrag relative w-full aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-muted/20 transition-colors disabled:opacity-30 disabled:pointer-events-none">
                                      {slot.icon}
                                      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/40"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                                    <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => inlineUploadRef.current?.click()}>
                                      <UploadIcon size={14} className="mr-1.5" />
                                      {t("Upload media inline")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openAssetPicker(slot.type)}>
                                      <LibraryBigIcon size={14} className="mr-1.5" />
                                      {t("Pick from library")}
                                    </DropdownMenuItem>
                                    {slot.type === 'image' ? (
                                      <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openSubjectPicker('reference')}>
                                        <UserSquare size={14} className="mr-1.5" />
                                        {t("Pick from subject library")}
                                      </DropdownMenuItem>
                                    ) : null}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <div className="pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 bg-black/30">{slot.label}</div>
                              </div>
                            ))}
                            {hasNonSlotRoom && canAddMore(effectiveInlineMedia, inlineAttachments, connectedCounts) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button type="button" className="relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/30 transition-colors" title={t("Upload media inline")}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                                  <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => inlineUploadRef.current?.click()}>
                                    <UploadIcon size={14} className="mr-1.5" />
                                    {t("Upload media inline")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openAssetPicker()}>
                                    <LibraryBigIcon size={14} className="mr-1.5" />
                                    {t("Pick from library")}
                                  </DropdownMenuItem>
                                  {effectiveInlineMedia.image?.allow ? (
                                    <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openSubjectPicker()}>
                                      <UserSquare size={14} className="mr-1.5" />
                                      {t("Pick from subject library")}
                                    </DropdownMenuItem>
                                  ) : null}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </>
                        );
                      })() : undefined}
                    />
                    );
                  })()}
                  {/* ── V2V: combined reference image + video upload slots ── */}
                  {currentFrameMode === 'v2v' && (() => {
                    const imageAllowed = effectiveInlineMedia.image?.allow === true;
                    const maxImages = effectiveInlineMedia.image?.maxCount ?? 1;
                    const maxVideos = effectiveInlineMedia.video?.maxCount ?? 1;
                    const canUploadImage = imageAllowed && v2vImageItems.length < maxImages && !isToolbarBlocked && !inlineUploading;
                    const canUploadVideo = v2vItems.length < maxVideos && !isToolbarBlocked && !inlineUploading;
                    const hasAny = v2vImageItems.length > 0 || v2vItems.length > 0 || canUploadImage || canUploadVideo || inlineUploading;
                    if (!hasAny) return null;
                    return (
                      <div className="px-0.5">
                        <div className="grid grid-cols-7 gap-1.5">
                          {v2vItems.map((item) => (
                            <div key={item.id} className="relative">
                              <div className="relative w-full aspect-square bg-muted/30 overflow-hidden group rounded-lg">
                                {item.url ? (
                                  <>
                                    <video
                                      ref={(el) => { if (el) refMediaEls.current.set(item.id, el); else refMediaEls.current.delete(item.id); }}
                                      src={item.url}
                                      muted loop playsInline preload="metadata"
                                      className="h-full w-full object-cover"
                                      onEnded={() => { if (playingRefId === item.id) setPlayingRefId(null); }}
                                    />
                                    <button
                                      type="button"
                                      className="absolute inset-0 flex items-center justify-center cursor-pointer z-[5]"
                                      onClick={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        const el = refMediaEls.current.get(item.id);
                                        if (!el) return;
                                        if (playingRefId === item.id) { (el as HTMLVideoElement).pause(); setPlayingRefId(null); }
                                        else {
                                          const prev = playingRefId ? refMediaEls.current.get(playingRefId) : null;
                                          if (prev) { prev.pause(); prev.currentTime = 0; }
                                          (el as HTMLVideoElement).play().catch(() => {});
                                          setPlayingRefId(item.id);
                                        }
                                      }}
                                    >
                                      {playingRefId !== item.id && (
                                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-black/50"><PlayIcon size={10} className="text-white" /></div>
                                      )}
                                    </button>
                                  </>
                                ) : (
                                  <div className="flex h-full w-full flex-col items-center justify-center gap-0.5">
                                    <VideoIcon size={12} className="text-muted-foreground/40" />
                                    <span className="text-[7px] leading-tight text-muted-foreground/40">{t('Pending')}</span>
                                  </div>
                                )}
                                {item.source === 'inline' && !isToolbarBlocked && (
                                  <button type="button" className="absolute top-0 left-0 h-4 w-4 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleInlineAttachmentsChange(inlineAttachments.filter((a) => a.id !== item.attId)); }}>
                                    <XIcon size={8} />
                                  </button>
                                )}
                                {item.source === 'upstream' && (
                                  <button type="button" className="absolute inset-0 cursor-pointer opacity-0 z-[4]" onClick={() => focusNodeById(item.sourceId!)} />
                                )}
                              </div>
                              <div className="pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 bg-black/50">{t('Video')}</div>
                            </div>
                          ))}
                          {canUploadVideo && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild disabled={isToolbarBlocked}>
                                <div className="relative">
                                  <button type="button" disabled={isToolbarBlocked} className="nodrag relative w-full aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-muted/20 transition-colors disabled:opacity-30 disabled:pointer-events-none">
                                    <VideoIcon size={12} className="text-sky-400/50" />
                                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/40"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                  </button>
                                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 bg-black/30">{t('Video')}</div>
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                                <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => inlineUploadRef.current?.click()}>
                                  <UploadIcon size={14} className="mr-1.5" />
                                  {t("Upload media inline")}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openAssetPicker('video')}>
                                  <LibraryBigIcon size={14} className="mr-1.5" />
                                  {t("Pick from library")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {v2vImageItems.map((item) => {
                            const displayUrl = item.previewUrl ?? item.url;
                            return (
                              <div key={item.id} className="relative">
                                <div className="relative w-full aspect-square bg-muted/30 overflow-hidden group rounded-lg">
                                  {displayUrl && !displayUrl.startsWith('Asset://') ? (
                                    <VariantImage url={displayUrl} transform={REF_THUMB_TRANSFORM} width={96} height={96} className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center bg-primary/5">
                                      <Package size={14} className="text-primary/60" />
                                    </div>
                                  )}
                                  {item.source === 'inline' && !isToolbarBlocked && (
                                    <button type="button" className="absolute top-0 left-0 h-4 w-4 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10" onClick={(e) => {
                                      e.preventDefault(); e.stopPropagation();
                                      rewriteInstructionsForRemoval((it) => it.sourceId === `inline-${item.attId}`);
                                      handleInlineAttachmentsChange(inlineAttachments.filter((a) => a.id !== item.attId));
                                    }}>
                                      <XIcon size={8} />
                                    </button>
                                  )}
                                  {item.source === 'upstream' && (
                                    <button type="button" className="absolute inset-0 cursor-pointer opacity-0 z-[4]" onClick={() => focusNodeById(item.sourceId!)} />
                                  )}
                                </div>
                                <div className="pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 bg-black/50">{t('Image')}</div>
                              </div>
                            );
                          })}
                          {canUploadImage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild disabled={isToolbarBlocked}>
                                <div className="relative">
                                  <button type="button" disabled={isToolbarBlocked} className="nodrag relative w-full aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-muted/20 transition-colors disabled:opacity-30 disabled:pointer-events-none">
                                    <ImageIcon size={12} className="text-muted-foreground/50" />
                                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/40"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                  </button>
                                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-none py-1 rounded-b-lg z-10 bg-black/30">{t('Image')}</div>
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                                <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => inlineUploadRef.current?.click()}>
                                  <UploadIcon size={14} className="mr-1.5" />
                                  {t("Upload media inline")}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openAssetPicker('image')}>
                                  <LibraryBigIcon size={14} className="mr-1.5" />
                                  {t("Pick from library")}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openSubjectPicker('reference')}>
                                  <UserSquare size={14} className="mr-1.5" />
                                  {t("Pick from subject library")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {inlineUploading && (
                            <div className="relative w-full aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex items-center justify-center">
                              <Loader2Icon size={12} className="animate-spin text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {/* ── Video/audio grid (upstream + inline non-image) ── */}
                  {(() => {
                    if (currentFrameMode === 'v2v') return null;
                    const isRefLike = currentFrameMode === 'ref' || currentFrameMode === 'avatar';
                    const refSourceIds = isRefLike ? new Set(orderedReferenceImages.filter((r) => r.source === 'upstream' && r.sourceId).map((r) => r.sourceId)) : null;
                    const refInlineIds = isRefLike ? new Set(orderedReferenceImages.filter((r) => r.source === 'inline' && r.attId).map((r) => r.attId!)) : null;
                    const filteredVideoAudio = (refSourceIds ? incomingVideoAudioInputs.filter((it) => !refSourceIds.has(it.sourceId)) : incomingVideoAudioInputs)
                      .filter((it) => effectiveInlineMedia[it.mediaType]?.allow !== false);
                    const inlineNonImage = inlineAttachments
                      .filter((a) => a.fileType !== 'image' && !(refInlineIds && refInlineIds.has(a.id)))
                      .filter((a) => effectiveInlineMedia[a.fileType as 'video' | 'audio']?.allow !== false);
                    const showMediaUploadBtn = !currentFrameMode && effectiveInlineMedia.allowInline && !isToolbarBlocked;
                    if (filteredVideoAudio.length === 0 && inlineNonImage.length === 0 && !showMediaUploadBtn) return null;
                    return (
                      <div className="w-full overflow-hidden">
                        <DndContext sensors={inlineSortSensors} collisionDetection={closestCenter} onDragEnd={handleInlineReorder}>
                          <SortableContext items={inlineNonImage.map((a) => a.id)} strategy={rectSortingStrategy}>
                            <div className="grid w-full grid-cols-8 gap-1.5">
                              {filteredVideoAudio.map((it) => (
                                <button key={`upstream-${it.mediaType}-${it.sourceId}`} type="button" className={cn("relative w-full aspect-square overflow-hidden rounded-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50", it.url ? "bg-muted/30" : "bg-muted/30 border border-dashed border-muted-foreground/20")} title={`${it.idx}. ${it.label}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); focusNodeById(it.sourceId); }}>
                                  {it.url ? (
                                    <div className="flex h-full w-full items-center justify-center bg-muted/40">
                                      {it.mediaType === 'video' ? <VideoIcon size={14} className="text-muted-foreground" /> : <MusicIcon size={14} className="text-muted-foreground" />}
                                    </div>
                                  ) : (
                                    <div className="flex h-full w-full flex-col items-center justify-center gap-0.5">
                                      {it.mediaType === 'video' ? <VideoIcon size={12} className="text-muted-foreground/40" /> : <MusicIcon size={12} className="text-muted-foreground/40" />}
                                      <span className="text-[7px] leading-tight text-muted-foreground/40">{t('Pending')}</span>
                                    </div>
                                  )}
                                  {it.idx > 0 && (
                                    <div className="pointer-events-none absolute top-0 right-0 h-5 w-5 bg-black/60 text-white text-[10px] font-semibold rounded-bl-full flex items-start justify-end pr-1 pt-0.5 leading-none">{it.idx}</div>
                                  )}
                                </button>
                              ))}
                              {inlineNonImage.map((att) => {
                                const isVideo = att.fileType === 'video';
                                return (
                                  <SortableGridItem key={`inline-${att.id}`} id={att.id} disabled={isToolbarBlocked}>
                                    <div className="relative w-full aspect-square bg-muted/30 overflow-hidden group rounded-lg" title={att.name || att.fileType}>
                                      <div className="flex h-full w-full items-center justify-center bg-muted/40">
                                        {isVideo ? <VideoIcon size={14} className="text-muted-foreground" /> : <MusicIcon size={14} className="text-muted-foreground" />}
                                      </div>
                                      {!isToolbarBlocked && (
                                        <button type="button" className="absolute top-0 left-0 h-5 w-5 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleInlineAttachmentsChange(inlineAttachments.filter((a) => a.id !== att.id), att); }}>
                                          <XIcon size={10} />
                                        </button>
                                      )}
                                    </div>
                                  </SortableGridItem>
                                );
                              })}
                              {showMediaUploadBtn && !inlineUploading && canAddMore(effectiveInlineMedia, inlineAttachments, connectedCounts) && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button type="button" className="relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/30 transition-colors" title={t("Upload media inline")}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                                    <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => inlineUploadRef.current?.click()}>
                                      <UploadIcon size={14} className="mr-1.5" />
                                      {t("Upload media inline")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => openAssetPicker()}>
                                      <LibraryBigIcon size={14} className="mr-1.5" />
                                      {t("Pick from library")}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              {showMediaUploadBtn && inlineUploading && (
                                <div className="relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
                                  <Loader2Icon size={13} className="animate-spin text-muted-foreground" />
                                </div>
                              )}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    );
                  })()}
                  {/* Hidden file inputs */}
                  <input
                    ref={inlineUploadRef}
                    type="file"
                    accept={buildAcceptFromConfig(effectiveInlineMedia)}
                    multiple
                    className="hidden"
                    onChange={(e) => void handleInlineUploadFiles(e.target.files)}
                  />
                  <input
                    ref={firstFrameInputRef}
                    type="file"
                    accept={(effectiveInlineMedia.firstFrame?.formats ?? effectiveInlineMedia.image?.formats)?.join(',') || 'image/*'}
                    className="hidden"
                    onChange={(e) => void handleFrameSlotUpload('start', e.target.files)}
                  />
                  <input
                    ref={lastFrameInputRef}
                    type="file"
                    accept={(effectiveInlineMedia.lastFrame?.formats ?? effectiveInlineMedia.image?.formats)?.join(',') || 'image/*'}
                    className="hidden"
                    onChange={(e) => void handleFrameSlotUpload('end', e.target.files)}
                  />
                  <div className={cn("relative w-full min-w-0", Boolean((selectedModel as any)?.hideInstructions) && "hidden")}>
                    {modelUi?.multi_shots ? (
                      <MultiShotEditor
                        value={localInstructions}
                        totalDuration={Number((data as any)?.duration) || 5}
                        onChange={(v) => {
                          setLocalInstructions(v);
                        }}
                        onFocus={() => setInstructionsFocused(true)}
                        onBlur={handleInstructionsBlur}
                        disabled={isToolbarBlocked}
                        imageInputs={currentFrameMode === 'none' ? [] : allImageInputsForMention}
                      />
                    ) : (
                      <InstructionInput
                        ref={instructionInputRef}
                        textareaRef={textareaRef}
                        value={localInstructions}
                        placeholder={currentFrameMode === 'none' ? t('Type / to summon prompts') : t('Type @ to reference media, / to summon prompts')}
                        onChange={handleInstructionsChange}
                        onInput={adjustTextareaHeight}
                        onFocus={() => setInstructionsFocused(true)}
                        onBlur={handleInstructionsBlur}
                        onCommit={(v) => {
                          setLocalInstructions(v);
                          updateNodeData(id, { instructions: v });
                        }}
                        showPlaceholder={!localInstructions.length}
                        imageInputs={currentFrameMode === 'none' ? [] : allImageInputsForMention}
                        hideExpandButton={frameModeOptions.length >= 1}
                      />
                    )}
                  </div>
                </div>
              )}
              topControls={configControls}
              middleControls={middleControls}
              bottomControls={actionControls}
              expandedPanel={
                advancedExpanded ? (
                  <AdvancedInlinePanel
                    id={id}
                    uiOptions={panelUiOptions}
                    modelUi={modelUi as any}
                    canUseAdvanced={canUseAdvanced}
                    onChange={(key: string, val: unknown) =>
                      updateNodeData(id, { modelUi: { ...(modelUi || {}), [key]: val } })
                    }
                  />
                ) : undefined
              }
            />
          ),
        },
      ];
    }, [
      id,
      data,
      modelId,
      selectedModel,
      hasIncomingVideoNodes,
      isToolbarBlocked,
      canUseAdvanced,
      project?.id,
      generating,
      handleGenerate,
      handleCancel,
      handleInstructionsChange,
      handleInstructionsBlur,
      adjustTextareaHeight,
      localInstructions,
      textareaRef,
      incomingImageInputs,
      incomingVideoAudioInputs,
      focusNodeById,
      inlineAttachments,
      handleInlineAttachmentsChange,
      handleInlineReorder,
      inlineSortSensors,
      allImageInputsForMention,
      inlineUploading,
      handleInlineUploadFiles,
      effectiveInlineMedia,
      frameModeOptions,
      currentFrameMode,
      handleFrameModeChange,
      frameImages,
      frameRoleSlots,
      handleFrameImageReorder,
      frameSlotUploading,
      handleFrameSlotUpload,
      uiOptions,
      advancedOpen,
      ec,
      orderedReferenceImages,
      handleReferenceReorder,
      connectedCounts,
      playingRefId,
      estimatedCredits,
      passRemaining,
      passCount,
      discountMultiplier,
      passDiscountMultiplier,
      modelAccessMap,
      userPlan,
      userInterval,
      userTags,
      planLevels,
      assetPreviewUrl,
    ]);

  const contextMenuItems = useMemo(() => {
    if (!availableVideoUrl) return undefined;
    return [
      {
        label: t("Extract first frame"),
        onClick: () => extractFrame("first"),
        icon: <ImageIcon size={12} />,
      },
      {
        label: t("Extract last frame"),
        onClick: () => extractFrame("last"),
        icon: <ImageIcon size={12} />,
      },
      {
        label: t("Extract frame at"),
        onClick: () => {
          try {
            window.dispatchEvent(
              new CustomEvent('open_video_frame_slider', { detail: { id } })
            );
          } catch {}
        },
        icon: <ImageIcon size={12} />,
      },
    ];
  }, [availableVideoUrl, extractFrame, id, t]);

  const bgStyle = NODE_BG_STYLE;

  useEffect(() => {
    setVidLoaded(false);
  }, [(data as any)?.generated?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  // 由“比例设置”计算 CSS 比例（如 "16:9" -> "16/9"）。aspectRatio 只承载比例设置，不再含分辨率。
  const aspectRatioValue = useMemo(() => {
    try {
      const raw = String((data as any)?.aspectRatio || "").trim();
      if (!raw) return undefined;
      if (/^\d+:\d+$/.test(raw)) return raw.replace(":", "/");
      if (/^\d+\/\d+$/.test(raw)) return raw;
      return undefined;
    } catch {
      return undefined;
    }
  }, [(data as any)?.aspectRatio]);
  // 真实分辨率（统一存于 generated.size，兼容旧的顶层 width/height）。集中解析一次，供比例与 <video> 内联尺寸复用。
  const realDimensions = useMemo(
    () => parseVideoDimensions(data),
    [(data as any)?.generated?.size, (data as any)?.width, (data as any)?.height],
  );
  // 与 image 的 displaySize 一致：优先使用视频真实分辨率，避免“设置比例”与“实际输出”
  // 不一致导致节点比例不更新；无真实尺寸时回退到用户设置的比例；再兜底默认值。
  const surfaceAspectRatio = useMemo(() => {
    if (realDimensions) {
      return `${realDimensions.width}/${realDimensions.height}`;
    }
    return aspectRatioValue ?? "854/480";
  }, [aspectRatioValue, realDimensions]);

  // Perf (Phase 9.7): merged single store subscription with shallow equality.
  const nodeSliceSelector = useMemo(
    () => (s: any) => {
      const n = s?.nodeLookup?.get?.(String(id));
      return {
        selected: Boolean(n?.selected),
        measuredHeight: n?.measured?.height,
      };
    },
    [id],
  );
  const { selected: isSelected, measuredHeight } = useStore(
    nodeSliceSelector,
    shallow,
  );
  const isMultiSelect = useIsMultiSelect();
  const insetClass = "inset-0";

  // 多视频选择器
  const generatedUrls = (data as any)?.generated?.urls as string[] | undefined;
  const generatedUrl = (data as any)?.generated?.url as string | undefined;
  const mainUrl = useMemo(() => {
    // Asset:// 引用不可直接播放，用提交审核时保存的预览 URL 代替；
    // 预览缺失时与 image 节点一致，回退到 generated.urls[0]。
    const resolved = resolveAssetRefPreview(generatedUrl, assetPreviewUrl);
    if (resolved) return resolved;
    if (Array.isArray(generatedUrls) && generatedUrls.length > 0) return generatedUrls[0];
    return undefined;
  }, [generatedUrl, generatedUrls, assetPreviewUrl]);
  const multiUrls = useMemo(() => (Array.isArray(generatedUrls) ? generatedUrls.filter(Boolean) : []), [generatedUrls]);
  // 首帧图（poster）：优先节点数据里的缩略图；否则从视频 URL 推导同目录 <base>_first.jpg。
  // 与 collapsed-summary 的逻辑保持一致。
  // Perf: 依赖用原始字段，避免 onLoadedData 写入 generated.size/sizeUrl 时整体重算。
  const thumbnailUrl = (data as any)?.generated?.thumbnailUrl as string | undefined;
  const coverUrl = (data as any)?.generated?.coverUrl as string | undefined;
  const previewImageUrl = (data as any)?.generated?.previewImageUrl as string | undefined;
  const contentUrl = (data as any)?.content?.url as string | undefined;
  const rawPosterUrl = useMemo(() => {
    const fromData =
      (typeof thumbnailUrl === 'string' && thumbnailUrl) ||
      (typeof coverUrl === 'string' && coverUrl) ||
      (typeof previewImageUrl === 'string' && previewImageUrl) ||
      undefined;
    // 同目录 <videoBase>_first.jpg（与服务端抽帧规则一致）
    const fromVideo = (videoUrl?: string): string | undefined => (videoUrl ? videoPosterUrl(videoUrl, 'first') : undefined);
    const raw =
      fromData ??
      fromVideo(mainUrl) ??
      fromVideo(typeof contentUrl === 'string' ? contentUrl : undefined);
    return raw || undefined;
  }, [thumbnailUrl, coverUrl, previewImageUrl, contentUrl, mainUrl]);
  // 优先 media 服务预生成的缩略图（xl 档，短边 1024，省带宽）；未生成时回退 poster 原图，再失败则隐藏
  // （exhausted 按 poster 地址 + 视频源记录：首帧可能来自节点数据、地址不随视频变，切换视频源时也重新尝试）。
  const {
    src: posterUrl,
    onError: onPosterError,
    exhausted: posterFailed,
  } = useImageVariant(rawPosterUrl, POSTER_TRANSFORM, { resetKey: mainUrl });
  const hasPoster = Boolean(posterUrl) && !posterFailed;
  // 切换视频源时重置 vidLoaded，避免旧视频的 vidLoaded=true 让新视频加载期间首帧图被隐藏、露出陈旧帧。
  useEffect(() => {
    setVidLoaded(false);
  }, [mainUrl]);
  const multiThumbHeightPx = useMemo(() => {
    try {
      const h = Number(measuredHeight ?? 240);
      const quarter = Math.floor(h / 4);
      return Math.max(72, Math.min(240, Number.isFinite(quarter) ? quarter : 72));
    } catch {
      return 72;
    }
  }, [measuredHeight]);
  const onMultiVideoSelect = useCallback(
    (url: string) => updateNodeData(id, { generated: { ...(data as any).generated, url } }),
    [id, updateNodeData, (data as any)?.generated],
  );
  const multiPicker = useMultiVideoPicker({
    urls: multiUrls,
    activeUrl: mainUrl,
    thumbHeightPx: multiThumbHeightPx,
    onSelectUrl: onMultiVideoSelect,
    disabled: isToolbarBlocked,
  });

  const inViewport = useInViewport(nodeVideoRef, mainUrl);
  // 拖拽/平移画布时不加载/解码视频，用首帧图顶着，移动结束再恢复（仅在有首帧图时抑制，避免无图节点拖动变空白）。
  const isInteracting = useIsCanvasPositionInteracting();
  const suppressForInteraction = isInteracting && hasPoster;
  // 有首帧图时：仅选中才加载真实视频（点击才播放/解码）；
  // 无首帧图时：退回原行为（进视口即加载），避免节点空白。
  const shouldLoadVideo =
    !suppressForInteraction && (isSelected || (!hasPoster && inViewport));
  // 视频正在加载/解码（尚未出画面）：此时显示转圈图标，隐藏播放符号。
  const isLoadingVideo = shouldLoadVideo && !vidLoaded;
  // 首帧图当前是否“可见”（视频尚未解码出画面）。用于播放符号的显示判断；
  // <Image> 本身的挂载/淡出由 hasPoster + vidLoaded 控制。
  const showPoster = hasPoster && !vidLoaded;

  // Unified play / pause / unload — avoids ordering races between separate effects.
  useEffect(() => {
    const sync = () => {
      const v = nodeVideoRef.current;
      if (!v) return;
      try {
        if (!shouldLoadVideo) {
          v.pause();
          setVidLoaded(false);
          v.load();
        } else if (isSelected && !previewOpen && document.visibilityState === "visible") {
          try { v.muted = true; } catch {}
          v.play().catch(() => {});
        } else {
          v.pause();
          if (!isSelected) v.currentTime = 0;
        }
      } catch {}
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("blur", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("blur", sync);
    };
  }, [isSelected, previewOpen, shouldLoadVideo]);


  // 同步滑块 -> 节点内视频进度（Transform 场景）
  useEffect(() => {
    const onSeek = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const sec = Number(e?.detail?.seconds);
        if (!Number.isFinite(sec) || sec < 0) return;
        const v = nodeVideoRef.current;
        if (!v) return;
        try { v.currentTime = Math.max(0, sec); } catch {}
      } catch {}
    };
    const onPause = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const v = nodeVideoRef.current;
        if (!v) return;
        try { v.pause(); } catch {}
      } catch {}
    };
    const onPlay = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const v = nodeVideoRef.current;
        if (!v) return;
        try { v.play().catch(() => {}); } catch {}
      } catch {}
    };
    window.addEventListener('video_seek_to', onSeek as any);
    window.addEventListener('video_pause', onPause as any);
    window.addEventListener('video_play', onPlay as any);
    return () => {
      window.removeEventListener('video_seek_to', onSeek as any);
      window.removeEventListener('video_pause', onPause as any);
      window.removeEventListener('video_play', onPlay as any);
    };
  }, [id]);

  // 打开预览
  useEffect(() => {
    const onOpenPreview = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        setPreviewOpen(true);
      } catch {}
    };
    window.addEventListener('open_video_preview', onOpenPreview as any);
    return () => window.removeEventListener('open_video_preview', onOpenPreview as any);
  }, [id]);

  return (
    <>
      <NodeLayout
        id={id}
        data={data}
        type={type}
        title={title}
        variant="modern"
        aspectRatio={surfaceAspectRatio}
        toolbar={videoToolbar}
        className={multiPicker.open ? 'overflow-visible' : undefined}
        collapsedAspectRatio="16/9"
        collapsedSummary={<VideoCollapsedSummary data={data as any} />}
        contextMenuItems={contextMenuItems}
      >
        {/* 多视频缩略图菜单 */}
        {multiUrls.length > 1 ? (
          <MultiVideoMenuPanel
            state={multiPicker}
            createNodeLabel={t('Create node')}
          />
        ) : null}
        {/* 顶部工具栏：只在选中时挂载，避免每个节点都订阅 selection/nodes */}
        {isSelected ? (
          <VideoTopToolbar
            nodeId={id}
            videoUrl={availableVideoUrl}
            data={data}
            isToolbarBlocked={isToolbarBlocked}
          />
        ) : null}
        {/* 生成中 */}
        {(data as any)?.status === "processing" && (
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <ProcessingSurface
              aspectRatio={surfaceAspectRatio}
              bgStyle={bgStyle}
              insetClass={insetClass}
              radiusClass="rounded-3xl"
              progress={progress}
              icon={<VideoIcon />}
              label={
                <div className="text-base font-medium text-muted-foreground text-center">
                  {progress < 100
                    ? t("Generating video… {{percent}}%", {
                        percent: Number(progress).toFixed(2),
                      })
                    : t("Generating video…")}
                </div>
              }
              footer={
                <div className="text-sm text-muted-foreground/80 text-center">
                  {Number(progress) >= 99 ? (
                    <div className="opacity-80">
                      <div>{t("eta_hint.line1")}</div>
                      <div>{t("eta_hint.line2")}</div>
                      <div>{t("eta_hint.line3")}</div>
                    </div>
                  ) : (
                    <div className="opacity-80">{hasEstimate ? t('Estimated wait {{time}}', { time: remainingText }) : t('Calculating wait time…')}</div>
                  )}
                </div>
              }
            />
          </div>
        )}
        {(data as any)?.status === "failed" && (
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <NodeSurface
              aspectRatio={surfaceAspectRatio}
              bgStyle={bgStyle}
              insetClass={insetClass}
              showDashed={false}
              radiusClass="rounded-3xl"
            >
              <div className="relative flex h-full items-center justify-center p-6">
                <div className="text-center max-w-[90%]">
                  <p className="inline-flex flex-wrap items-center justify-center gap-2 text-sm font-medium text-foreground">
                    <span>{t("Video generation failed")}</span>
                    <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                      {t("Credits refunded")}
                    </span>
                  </p>
                  {(data as any)?.error ? (
                    <p className="mt-1 text-xs text-muted-foreground break-words whitespace-pre-wrap">
                      {String((data as any).error)}
                    </p>
                  ) : null}
                </div>
              </div>
            </NodeSurface>
          </div>
        )}
        {(data as any)?.status !== "processing" &&
          (data as any)?.status !== "failed" &&
          !(data as any)?.generated?.url && (
            <div
              className="relative"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <NodeSurface
                aspectRatio={surfaceAspectRatio}
                bgStyle={bgStyle}
                insetClass={insetClass}
                showDashed={false}
                radiusClass="rounded-3xl"
                // overlayTopRight removed to match image node (no palette)
              >
                <div className="relative flex h-full items-center justify-center p-6">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <p className="text-base font-medium text-foreground">
                      {t("Ready to Create?")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Zap size={14} className="opacity-80" />
                        {t("Click Generate to create a video")}
                      </span>
                    </p>
                  </div>
                </div>
              </NodeSurface>

              {/* 引导按钮固定在外层容器底部，脱离 NodeSurface 的 inset 过渡，选中时不会产生滑动 */}
              {isSelected && !isMultiSelect && Boolean(modelCapabilities.imageSuggestion || modelCapabilities.videoSuggestion) ? (
                <div className="pointer-events-auto absolute inset-x-0 bottom-6 flex justify-center px-6">
                  <VideoGuidance
                    guidance={modelCapabilities}
                    onAddImageNode={handleAddImageNode}
                    onAddVideoNode={handleAddVideoNode}
                    t={t}
                  />
                </div>
              ) : null}
            </div>
          )}
        {(data as any)?.status !== "processing" &&
          (data as any)?.status !== "failed" &&
          mainUrl && (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <NodeSurface
                className="cursor-zoom-in"
                aspectRatio={surfaceAspectRatio}
                bgStyle={bgStyle}
                insetClass={insetClass}
                showDashed={false}
                radiusClass="rounded-3xl"
              >
                {/* 多视频按钮 */}
                {multiUrls.length > 1 ? (
                  <MultiVideoTrigger
                    state={multiPicker}
                    ariaLabel={t('Videos')}
                    widthPx={100}
                    onTriggerClick={() => {
                      try {
                        const n = getNode(id);
                        if (n && !n.selected) {
                          (n as any).selected = true;
                        }
                      } catch {}
                    }}
                  />
                ) : null}
                <PreviewVideoOnClick
                  className="absolute inset-0"
                  onPreview={() => setPreviewOpen(true)}
                  enabled={isSelected}
                >
                  <EditWithPinturaVideoOnDoubleClick
                    className="absolute inset-0"
                    src={mainUrl}
                    onUpdated={({ url, type, width, height, durationSeconds }) => {
                      // 真实分辨率统一写入 generated.size（"宽x高"）+ sizeUrl（绑定新 url），与 image 一致。
                      // 拿不到尺寸时清空旧的 size/sizeUrl，避免沿用编辑前视频的尺寸；由 onLoadedData 重测补齐。
                      const hasDims =
                        Number.isFinite(width) && Number.isFinite(height) && (width as number) > 0 && (height as number) > 0;
                      const patch: any = {
                        generated: {
                          ...(data as any).generated,
                          url,
                          type,
                          size: hasDims ? `${width}x${height}` : undefined,
                          sizeUrl: hasDims ? url : undefined,
                        },
                        content: { url, type },
                        sourceUrl: url,
                        updatedAt: new Date().toISOString(),
                      };
                      if (Number.isFinite(durationSeconds) && (durationSeconds as number) > 0) {
                        patch.durationSeconds = durationSeconds;
                      }
                      updateNodeData(id, patch);
                      toast.success(t("Video updated"));
                    }}
                  >
                    <video
                      ref={nodeVideoRef}
                      src={shouldLoadVideo ? mainUrl : undefined}
                      preload={isSelected ? undefined : 'metadata'}
                      width={realDimensions?.width ?? 1280}
                      height={realDimensions?.height ?? 720}
                      muted
                      loop
                      playsInline
                    onLoadedData={() => {
                      try {
                        setVidLoaded(true);
                        const v = nodeVideoRef.current as HTMLVideoElement | null;
                        const vw = v?.videoWidth;
                        const vh = v?.videoHeight;
                        const vd = v?.duration;
                          if (
                            Number.isFinite(vw as number) &&
                            Number.isFinite(vh as number) &&
                            (vw as number) > 0 &&
                            (vh as number) > 0
                          ) {
                          // 绑定到实际加载的 url（mainUrl 即 <video src>），而非可能不一致的 generated.url。
                          const curUrl = mainUrl ?? ((data as any)?.generated?.url as string | undefined);
                          // 真实分辨率统一存于 generated.size（"宽x高"）+ sizeUrl，与 image 完全一致。
                          const sizeStr = `${vw}x${vh}`;
                          const prevG = (data as any)?.generated ?? {};
                          const next: any = {};
                          if (prevG.size !== sizeStr || prevG.sizeUrl !== curUrl) {
                            next.generated = { ...prevG, size: sizeStr, sizeUrl: curUrl };
                          }
                          // durationSeconds 作为视频独有的时间维度单独保留（image 无此概念）。
                          if (Number.isFinite(vd as number) && (vd as number) > 0) {
                            const curDur = Number((data as any)?.durationSeconds);
                            const rounded = Math.round((vd as number) * 100) / 100;
                            if (!Number.isFinite(curDur) || curDur !== rounded) {
                              next.durationSeconds = rounded;
                            }
                          }
                          // 把测得的真实尺寸/时长回写进当前激活的历史条目（绑定其 url）：
                          // 服务端不返回视频尺寸，靠此处测量补齐；写入后切换历史即可直接读取、无需再次加载测量。
                          const activeId = (data as any)?.activeHistoryId as string | undefined;
                          const histPatch: Record<string, unknown> = { size: sizeStr };
                          if (next.durationSeconds != null) histPatch.durationSeconds = next.durationSeconds;
                          {
                            const prevHistory = (data as any)?.history as any[] | undefined;
                            const nextHistory = patchHistoryItemById(prevHistory, activeId, curUrl, histPatch);
                            if (nextHistory !== prevHistory) next.history = nextHistory;
                          }
                          if (Object.keys(next).length > 0) {
                            next.updatedAt = new Date().toISOString();
                            updateNodeData(id, next);
                          }
                        }
                        if (v && isSelected) {
                          try { v.muted = true; v.play().catch(() => {}); } catch {}
                        }
                      } catch {}
                    }}
                      className="w-full h-full object-cover"
                    />
                    {/* 首帧图：盖在视频上层。未选中时完全代替 <video>（省解码器/内存/带宽），
                        选中加载完成后淡出做交叉过渡。走 next/image 复用图片优化与缓存。
                        lazy 保证离屏不发请求；同一帧交叉淡出避免闪烁。 */}
                    {hasPoster && posterUrl && (
                      <Image
                        src={posterUrl}
                        alt=""
                        width={realDimensions?.width ?? 1280}
                        height={realDimensions?.height ?? 720}
                        loading="lazy"
                        draggable={false}
                        // 预生成缩略图直接加载（缓存头生效：被覆盖的首帧缩略图只缓存 1 天）；Supabase 首帧原图走
                        // /_next/image 优化；第三方 provider 域名未配置在 next.config images 里，直通以免报错。
                        unoptimized={shouldSkipNextImageOptimization(posterUrl)}
                        onError={onPosterError}
                        // 视频解码出画面（vidLoaded）后淡出，与底层视频交叉淡出避免闪烁；
                        // 失焦/卸载时（!vidLoaded）瞬间显示、不淡入，避免视频已卸载而首帧还在淡入造成的闪烁。
                        className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${
                          vidLoaded ? "opacity-0 transition-opacity duration-300" : "opacity-100"
                        }`}
                      />
                    )}
                    {isLoadingVideo && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-black/45">
                          <Loader2Icon size={26} className="animate-spin text-white opacity-90" />
                          <span className="absolute top-full mt-2 whitespace-nowrap text-xs text-white opacity-90 drop-shadow">
                            {t("Loading video")}
                          </span>
                        </div>
                      </div>
                    )}
                  </EditWithPinturaVideoOnDoubleClick>
                </PreviewVideoOnClick>
                {/* 展示静态首帧时（未选中，或拖拽/平移导致视频被抑制）显示播放符号；
                    视频真正在播（vidLoaded）或正在加载（显示转圈）时隐藏。 */}
                {(!isSelected || showPoster) && !isLoadingVideo && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/45">
                      <PlayIcon size={26} className="text-white opacity-90" />
                    </div>
                  </div>
                )}
              </NodeSurface>
            </div>
          )}
        {/* 已迁移到 TwoRowToolbar 的 InstructionInput，这里移除独立区域 */}
      </NodeLayout>
      <VideoPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        url={mainUrl || ""}
        titleClose={t("Close preview")}
        titleExtract={t("Extract current frame")}
        onExtractCurrentFrame={async (sec) => {
          const src = mainUrl || "";
          if (!src) return;
          await extractToImageNodeAt(id, sec, src);
        }}
      />
    <SubjectPickerDialog
      open={subjectPickerOpen}
      onOpenChange={setSubjectPickerOpen}
      onPick={handlePickFromSubjectLibrary}
    />
    <PrecheckDialog
      missing={precheckMissing}
      onOpenChange={(open) => { if (!open) setPrecheckMissing(null); }}
    />
    <TextModeImageDialog
      open={t2vImageDialogOpen}
      onOpenChange={setT2vImageDialogOpen}
      mediaKind="video"
      onKeep={() => { void generate({ interactive: true }); }}
      onSwitch={() => {
        handleFrameModeChange('first');
        void generate({ interactive: true, overrideData: { frameMode: 'first' } });
      }}
    />
    </>
  );
});
VideoTransform.displayName = 'VideoTransform';
